import React, { useCallback, useEffect, useMemo, useRef, startTransition, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  AreaChart,
  Area,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import {
  C,
  WS_GLOBAL_STYLE,
  parseCSV,
  inferTypes,
  colStats,
  getHistogram,
  getValueCounts,
  pearson,
  MetricTile,
  Card,
  CardHead,
  Btn,
  Sel,
  TxtInput,
  ChartTip,
  PinContext,
} from './labs/wsShared';
import DataTable from './labs/DataTable';
import CleaningLab from './labs/CleaningLab';
import MLLab from './labs/MLLab';
import { startUsageSession, usageDone, usageStep, usageWorkflowFromTabs } from '../utils/usageTracker';
import { trackAction } from '../utils/actionStore';
import PublishExperimentModal from './ui/PublishExperimentModal';
import HFDatasetPicker from './HFDatasetPicker';
import { idbSave, idbLoad, validateRowHeaders } from './data/idbStore';
import { getWorkspaceActionSummary } from './data/workspaceVersioning';
import { workspace as workspaceApi } from '../api/client';

const DL_TABS = (ds) => [
  { id: 'data', label: 'DATA SOURCE', icon: '📁', color: C.cyan },
  { id: 'profile', label: 'PROFILE', icon: '◈', color: C.cyan, disabled: !ds },
  { id: 'clean', label: 'CLEANING', icon: '◉', color: C.orange, disabled: !ds },
  { id: 'feature', label: 'FEATURE ENGINEERING', icon: '⬟', color: C.green, disabled: !ds },
  { id: 'network', label: 'NETWORK ARCHITECTURE', icon: '🧠', color: C.violet, disabled: !ds },
  { id: 'init', label: 'INITIALIZATION', icon: '⚙️', color: C.gold, disabled: !ds },
  { id: 'train', label: 'TRAIN', icon: '🏋️', color: C.orange, disabled: !ds },
  { id: 'test', label: 'TEST', icon: '🧪', color: C.cyan, disabled: !ds },
  { id: 'report', label: 'REPORT', icon: '📄', color: C.violet },
];

const DL_USAGE_WORKFLOW = usageWorkflowFromTabs([
  { id: 'upload', label: 'UPLOAD' },
  { id: 'profile', label: 'PROFILE' },
  { id: 'clean', label: 'CLEANING' },
  { id: 'feature', label: 'FEATURES' },
  { id: 'network', label: 'NETWORK' },
  { id: 'init', label: 'INIT' },
  { id: 'train', label: 'TRAIN' },
  { id: 'test', label: 'TEST' },
  { id: 'report', label: 'REPORT' },
]);

const SAMPLE_DATASETS = [
  {
    name: 'Iris.csv',
    url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/iris.csv',
    desc: '150 rows · 5 cols · multiclass classification',
  },
  {
    name: 'Titanic.csv',
    url: 'https://raw.githubusercontent.com/datasciencedojo/datasets/master/titanic.csv',
    desc: '891 rows · 12 cols · survival prediction',
  },
  {
    name: 'WineQuality.csv',
    url: 'https://raw.githubusercontent.com/selva86/datasets/master/Wine.csv',
    desc: '178 rows · 14 cols · multiclass',
  },
  {
    name: 'Tips.csv',
    url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/tips.csv',
    desc: '244 rows · 7 cols · regression',
  },
];

const ACTIVATION_OPTIONS = ['gelu', 'relu', 'tanh', 'sigmoid', 'leaky_relu', 'swish'];

const DEFAULT_FEATURE_CFG = {
  target: '',
  features: [],
  scaling: 'zscore',
  task: 'auto',
  split: 0.2,
};

const DEFAULT_NETWORK_CFG = {
  family: 'ann',
  hiddenLayers: '32,16',
  hiddenActivations: '',
  activation: 'relu',
  outputActivation: 'auto',
};

const DEFAULT_TRAIN_CFG = {
  initializer: 'xavier',
  optimizer: 'adam',
  lr: 0.01,
  useCustomLr: false,
  valSplit: 0.2,
  epochs: 35,
  batchSize: 16,
  momentum: 0.9,
};

const OPTIMIZER_OPTIONS = [
  { value: 'adam', label: 'ADAM' },
  { value: 'adamw', label: 'ADAMW' },
  { value: 'nadam', label: 'NADAM' },
  { value: 'amsgrad', label: 'AMSGRAD' },
  { value: 'momentum', label: 'MOMENTUM' },
  { value: 'nesterov', label: 'NESTEROV' },
  { value: 'rmsprop', label: 'RMSPROP' },
  { value: 'sgd', label: 'SGD' },
];

const DL_TAB_GROUP = {
  data: 'data',
  profile: 'data',
  clean: 'data',
  feature: 'feature',
  network: 'model',
  init: 'model',
  train: 'train',
  test: 'eval',
  report: 'report',
};

const mono = 'JetBrains Mono,monospace';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value));

const fnum = (value, digits = 4) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return value.toFixed(digits);
};

const normText = (value) => String(value ?? '').trim();

function fmtBytes(b) {
  if (!b) return '—';
  if (b >= 1_048_576) return (b / 1_048_576).toFixed(1) + ' MB';
  if (b >= 1024) return Math.round(b / 1024) + ' KB';
  return b + ' B';
}

const randomBetween = (a, b) => a + Math.random() * (b - a);

function profileDataset(ds) {
  const { rows, headers } = ds;
  if (!rows?.length || !headers?.length) return null;

  const types = inferTypes(headers, rows);
  const numCols = types.filter((t) => t.type === 'numeric').map((t) => t.col);
  const catCols = types.filter((t) => t.type === 'categorical').map((t) => t.col);

  const nullMap = {};
  headers.forEach((header) => {
    nullMap[header] = rows.filter((row) => row[header] === '' || row[header] == null).length;
  });
  const totalNulls = Object.values(nullMap).reduce((sum, count) => sum + count, 0);
  const nullPct = totalNulls > 0 ? +((totalNulls / (rows.length * headers.length)) * 100).toFixed(2) : 0;

  const rowKeys = rows.map((row) => headers.map((header) => row[header]).join('\x00'));
  const dupCount = rowKeys.length - new Set(rowKeys).size;

  const colProfiles = headers.map((header) => {
    const t = types.find((typeInfo) => typeInfo.col === header);
    const nullN = nullMap[header];
    const nullP = +((nullN / rows.length) * 100).toFixed(1);
    const stats = t?.type === 'numeric' ? colStats(rows, header) : null;
    const valueCounts = t?.type === 'categorical' ? getValueCounts(rows, header, 8) : null;
    const hist = t?.type === 'numeric' ? getHistogram(rows, header, 16) : null;

    const isId = t?.type === 'numeric' && t.uniqueCount / rows.length > 0.92 && rows.length > 30;
    const isConst = t?.uniqueCount <= 1;
    const isHighCard = t?.type === 'categorical' && t.uniqueCount > rows.length * 0.5 && rows.length > 50;
    const isBinary = t?.uniqueCount === 2;
    const flags = [
      isConst && { label: 'CONSTANT', color: C.red },
      isId && { label: 'LIKELY ID', color: '#fbbf24' },
      isHighCard && { label: 'HIGH CARD', color: '#fbbf24' },
      isBinary && { label: 'BINARY', color: C.cyan },
      nullP > 50 && { label: `${nullP}% NULL`, color: C.red },
      nullP > 10 && nullP <= 50 && { label: `${nullP}% NULL`, color: '#fbbf24' },
    ].filter(Boolean);

    let shape = null;
    if (stats) {
      const values = rows.map((row) => parseFloat(row[header])).filter((value) => !isNaN(value));
      const rawKurt = values.length > 3
        ? values.reduce((sum, value) => sum + Math.pow((value - stats.mean) / Math.max(stats.std, 1e-9), 4), 0) / values.length - 3
        : 0;
      shape = {
        skewLabel: Math.abs(stats.skew) < 0.5 ? 'Normal' : stats.skew > 0 ? 'Right-skew' : 'Left-skew',
        kurtosis: +rawKurt.toFixed(3),
        outlierPct: +((stats.outlierCount / Math.max(stats.count, 1)) * 100).toFixed(1),
        cv: stats.mean !== 0 ? +(Math.abs(stats.std / stats.mean) * 100).toFixed(1) : null,
      };
    }

    return {
      col: header,
      type: t?.type || '?',
      uniqueCount: t?.uniqueCount || 0,
      nullCount: nullN,
      nullPct: nullP,
      stats,
      valueCounts,
      hist,
      shape,
      flags,
    };
  });

  const corrCols = numCols.slice(0, 12);
  const corrSample = rows.slice(0, 3000);
  const topCorr = [];
  for (let i = 0; i < corrCols.length; i += 1) {
    for (let j = i + 1; j < corrCols.length; j += 1) {
      const r = pearson(corrSample, corrCols[i], corrCols[j]);
      topCorr.push({ a: corrCols[i], b: corrCols[j], r, abs: Math.abs(r) });
    }
  }
  topCorr.sort((a, b) => b.abs - a.abs);

  const qualityScore = Math.max(0, Math.round(
    100
    - nullPct * 1.2
    - (dupCount / Math.max(rows.length, 1)) * 25
    - colProfiles.filter((p) => p.flags.some((f) => f.label === 'CONSTANT')).length * 5
    - colProfiles.filter((p) => p.nullPct > 30).length * 3
    - (numCols.length === 0 ? 10 : 0)
  ));

  return {
    types,
    numCols,
    catCols,
    nullMap,
    nullPct,
    dupCount,
    totalNulls,
    colProfiles,
    topCorr,
    qualityScore,
    meta: {
      rows: rows.length,
      cols: headers.length,
      numeric: numCols.length,
      categorical: catCols.length,
      completeRows: rows.filter((row) => headers.every((header) => row[header] && row[header] !== '')).length,
    },
  };
}

const Pill = ({ label, color = C.cyan, size = 8 }) => (
  <span style={{ fontSize: size, padding: '2px 6px', background: `${color}18`, border: `1px solid ${color}40`, borderRadius: 2, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>
);

const MetCard = ({ label, value, color = C.cyan, sub, large }) => (
  <div style={{ padding: '12px 14px', background: C.raised, border: `1px solid ${C.b1}`, borderLeft: `2px solid ${color}`, borderRadius: 3 }}>
    <div style={{ fontSize: 7.5, color: C.t4, letterSpacing: '.12em', fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: large ? 26 : 20, color, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{sub}</div>}
  </div>
);

const CTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '8px 12px', borderRadius: 4, fontSize: 11 }}>
      <div style={{ color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>{label}</div>
      {payload.map((point, index) => (
        <div key={index} style={{ color: point.color || C.cyan }}>{point.name}: <b>{typeof point.value === 'number' ? point.value.toFixed(4) : point.value}</b></div>
      ))}
    </div>
  );
};

function QualityRing({ score, size = 80 }) {
  const color = score >= 80 ? C.green : score >= 60 ? '#fbbf24' : C.red;
  const r = size * 0.37;
  const cx = size / 2;
  const cy = size / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={`${color}22`} strokeWidth={size * 0.1} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={size * 0.1} strokeDasharray={`${c * (score / 100)} ${c * (1 - score / 100)}`} strokeDashoffset={c * 0.25} strokeLinecap="round" style={{ transition: 'stroke-dasharray .6s ease' }} />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={size * 0.21} fontWeight="900" fontFamily="Syne,sans-serif">{score}</text>
    </svg>
  );
}

const SectionHead = ({ label, color = C.cyan }) => (
  <div style={{ fontSize: 9, color, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.13em', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ height: 1, width: 18, background: color, opacity: 0.5 }} />
    {label}
    <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${color}44,transparent)` }} />
  </div>
);

function ProfileTab({ ds, profile }) {
  const { meta, qualityScore, colProfiles, nullMap, nullPct, dupCount, topCorr } = profile;
  const [view, setView] = useState('schema');

  const nullData = ds.headers
    .map((header) => ({ col: header.length > 16 ? `${header.slice(0, 14)}…` : header, full: header, nulls: nullMap[header], pct: +((nullMap[header] / ds.rows.length) * 100).toFixed(1) }))
    .sort((a, b) => b.nulls - a.nulls)
    .filter((d) => d.nulls > 0);

  const typeChartData = [
    { name: 'Numeric', value: meta.numeric, fill: C.cyan },
    { name: 'Categorical', value: meta.categorical, fill: '#fbbf24' },
  ].filter((d) => d.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(6,1fr)', gap: 10, alignItems: 'stretch' }}>
        <div style={{ padding: '10px 14px', background: C.raised, border: `1px solid ${C.b1}`, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <QualityRing score={qualityScore} size={72} />
          <div style={{ fontSize: 7.5, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em' }}>QUALITY</div>
        </div>
        <MetCard label="Rows" value={meta.rows.toLocaleString()} color={C.cyan} />
        <MetCard label="Columns" value={meta.cols} color={C.green} />
        <MetCard label="Numeric Cols" value={meta.numeric} color={C.violet} />
        <MetCard label="Cat Cols" value={meta.categorical} color="#fbbf24" />
        <MetCard label="Null Cells" value={`${nullPct}%`} color={nullPct > 20 ? C.red : nullPct > 5 ? '#fbbf24' : C.green} sub={`${profile.totalNulls.toLocaleString()} cells`} />
        <MetCard label="Duplicates" value={dupCount.toLocaleString()} color={dupCount > 0 ? '#fbbf24' : C.green} />
      </div>

      <div style={{ display: 'flex', gap: 2, background: C.deep, border: `1px solid ${C.b0}`, borderRadius: 4, padding: 3, flexWrap: 'wrap' }}>
        {[
          ['schema', 'COLUMN SCHEMA'],
          ['nullmap', 'NULL MAP'],
          ['sample', 'DATA PREVIEW'],
          ['correlations', 'CORRELATIONS'],
        ].map(([id, lbl]) => (
          <button key={id} onClick={() => setView(id)} style={{ padding: '6px 14px', border: 'none', borderRadius: 3, cursor: 'pointer', background: view === id ? C.cyan : 'transparent', color: view === id ? C.void : C.t2, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.09em' }}>{lbl}</button>
        ))}
      </div>

      {view === 'schema' && (
        <div>
          <SectionHead label="FULL COLUMN PROFILE" />
          <div style={{ overflowX: 'auto', border: `1px solid ${C.b1}`, borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 900 }}>
              <thead>
                <tr style={{ background: C.deep }}>
                  {['#', 'Column', 'Type', 'Unique', 'Nulls', 'Null%', 'Min', 'Max', 'Mean', 'Std Dev', 'Skew', 'Outliers%', 'Flags'].map((h) => (
                    <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.07em', fontWeight: 700, borderBottom: `1px solid ${C.b1}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colProfiles.map((p, i) => (
                  <tr key={p.col} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 ? `${C.raised}60` : 'transparent' }}>
                    <td style={{ padding: '6px 10px', color: C.t4, fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }}>{i}</td>
                    <td style={{ padding: '6px 10px', color: C.t1, fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.col}</td>
                    <td style={{ padding: '6px 10px' }}><Pill label={p.type === 'numeric' ? 'NUM' : 'CAT'} color={p.type === 'numeric' ? C.cyan : '#fbbf24'} /></td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t2 }}>{p.uniqueCount.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: p.nullCount > 0 ? C.red : C.t4 }}>{p.nullCount.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: p.nullPct > 20 ? C.red : p.nullPct > 5 ? '#fbbf24' : C.t4 }}>{p.nullPct}%</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t3, fontSize: 9 }}>{p.stats?.min ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t3, fontSize: 9 }}>{p.stats?.max ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t2, fontSize: 9 }}>{p.stats?.mean ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t2, fontSize: 9 }}>{p.stats?.std ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: Math.abs(p.stats?.skew || 0) > 1 ? '#fbbf24' : C.t3, fontSize: 9 }}>{p.stats?.skew ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: p.shape?.outlierPct > 5 ? C.red : C.t3, fontSize: 9 }}>{p.shape?.outlierPct ?? '—'}</td>
                    <td style={{ padding: '6px 10px' }}>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {p.flags.length === 0
                          ? <Pill label="✓ CLEAN" color={C.green} />
                          : p.flags.map((f, j) => <Pill key={j} label={f.label} color={f.color} />)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'nullmap' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
          <div>
            <SectionHead label="NULL COUNT PER COLUMN" />
            {nullData.length > 0 ? (
              <div style={{ height: Math.min(320, nullData.length * 26 + 60) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={nullData} layout="vertical" margin={{ top: 4, right: 14, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.b1} horizontal={false} />
                    <XAxis type="number" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={(v) => `${v}`} />
                    <YAxis type="category" dataKey="col" width={110} tick={{ fill: C.t2, fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }} />
                    <Tooltip content={<CTip />} formatter={(v, n, p) => [`${v} rows (${p.payload.pct}%)`, 'Null count']} />
                    <Bar dataKey="nulls" radius={[0, 3, 3, 0]}>
                      {nullData.map((d, i) => <Cell key={i} fill={d.pct > 20 ? C.red : d.pct > 5 ? '#fbbf24' : C.green} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ padding: 30, textAlign: 'center', background: C.raised, borderRadius: 4, border: `1px solid ${C.green}30` }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                <div style={{ color: C.green, fontSize: 12, fontWeight: 600 }}>No null values in dataset</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SectionHead label="SUMMARY" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                ['Total null cells', `${profile.totalNulls.toLocaleString()} / ${(ds.rows.length * ds.headers.length).toLocaleString()}`, nullPct > 10 ? C.red : '#fbbf24'],
                ['Null %', `${nullPct}%`, nullPct > 20 ? C.red : nullPct > 5 ? '#fbbf24' : C.green],
                ['Duplicate rows', dupCount.toLocaleString(), dupCount > 0 ? '#fbbf24' : C.green],
                ['Complete rows', `${((meta.completeRows / ds.rows.length) * 100).toFixed(1)}%`, C.cyan],
                ['Cols with any null', nullData.length.toString(), nullData.length > 0 ? '#fbbf24' : C.green],
                ['Cols >50% null', colProfiles.filter((p) => p.nullPct > 50).length.toString(), C.red],
              ].map(([lbl, val, color]) => (
                <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: C.raised, borderRadius: 3 }}>
                  <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{lbl}</span>
                  <span style={{ fontSize: 10, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{val}</span>
                </div>
              ))}
            </div>
            <div style={{ height: 160, marginTop: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="35%" outerRadius="65%" paddingAngle={3}>
                    {typeChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip content={<CTip />} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 9 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {view === 'sample' && <DataTable rows={ds.rows} headers={ds.headers} types={profile.types} showControls />}

      {view === 'correlations' && (
        <div>
          <SectionHead label="TOP PEARSON CORRELATIONS" />
          {topCorr.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {topCorr.slice(0, 15).map((p, i) => {
                const col = Math.abs(p.r) > 0.7 ? (p.r > 0 ? C.green : C.red) : Math.abs(p.r) > 0.4 ? '#fbbf24' : C.t3;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: C.raised, borderRadius: 3, border: `1px solid ${C.b1}` }}>
                    <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', minWidth: 14 }}>#{i + 1}</span>
                    <span style={{ fontSize: 10, color: C.t1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.a}</span>
                    <span style={{ fontSize: 9, color: C.t4 }}>↔</span>
                    <span style={{ fontSize: 10, color: C.t1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.b}</span>
                    <div style={{ width: 80, height: 5, background: C.b1, borderRadius: 3, flexShrink: 0 }}>
                      <div style={{ width: `${Math.abs(p.r) * 100}%`, height: '100%', background: col, borderRadius: 3 }} />
                    </div>
                    <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, fontWeight: 700, color: col, minWidth: 50, textAlign: 'right' }}>r={p.r.toFixed(3)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: 30, textAlign: 'center', color: C.t4 }}>Need at least 2 numeric columns for correlation analysis.</div>
          )}
        </div>
      )}
    </div>
  );
}

function inferTask(ds, target) {
  if (!ds || !target) return 'classification';
  const vals = ds.rows.map((row) => row[target]).filter((v) => v !== '' && v != null);
  if (!vals.length) return 'classification';
  const unique = new Set(vals);
  const numericRatio = vals.filter((v) => !Number.isNaN(parseFloat(v))).length / Math.max(vals.length, 1);
  if (numericRatio > 0.98) {
    if (unique.size <= 10) return 'classification';
    if (unique.size >= Math.max(12, Math.floor(vals.length * 0.05))) return 'regression';
    return 'regression';
  }
  return 'classification';
}

function parseLayerString(raw, fallback = [32, 16]) {
  const arr = String(raw || '')
    .split(',')
    .map((n) => parseInt(n.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0 && n <= 1024);
  return arr.length ? arr : fallback;
}

function parseHiddenActivations(raw, hiddenCount, fallback = 'relu') {
  const arr = String(raw || '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => ACTIVATION_OPTIONS.includes(token));
  const out = [];
  for (let i = 0; i < hiddenCount; i += 1) out.push(arr[i] || fallback);
  return out;
}

function weightHistogram(values, bins = 24) {
  if (!values?.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [{ range: `${min.toFixed(3)}`, count: values.length }];
  const width = (max - min) / bins;
  const counts = Array(bins).fill(0);
  values.forEach((value) => {
    const idx = Math.min(bins - 1, Math.max(0, Math.floor((value - min) / width)));
    counts[idx] += 1;
  });
  return counts.map((count, index) => {
    const lo = min + index * width;
    const hi = lo + width;
    return {
      range: `${lo.toFixed(2)}..${hi.toFixed(2)}`,
      count,
      center: (lo + hi) / 2,
    };
  });
}

function resolveOptimizerFamily(opt) {
  const key = String(opt || 'sgd').toLowerCase();
  if (['adam', 'adamw', 'nadam', 'amsgrad'].includes(key)) return 'adam';
  if (['momentum', 'nesterov'].includes(key)) return 'momentum';
  if (['rmsprop'].includes(key)) return 'rmsprop';
  return 'sgd';
}

function buildEncodedDataset(ds, featureCfg) {
  if (!ds?.rows?.length || !featureCfg?.target || !(featureCfg.features || []).length) return null;

  const rows = ds.rows;
  const headers = ds.headers;
  const types = inferTypes(headers, rows);
  const typeMap = Object.fromEntries(types.map((t) => [t.col, t.type]));
  const features = featureCfg.features.filter((f) => f !== featureCfg.target);
  const target = featureCfg.target;
  const task = featureCfg.task === 'auto' ? inferTask(ds, target) : featureCfg.task;

  const colEncoders = {};
  features.forEach((col) => {
    if (typeMap[col] === 'numeric') return;
    const uniq = [...new Set(rows.map((r) => normText(r[col])).filter(Boolean))];
    const map = {};
    uniq.forEach((v, idx) => {
      map[v] = idx;
    });
    colEncoders[col] = { map, scale: Math.max(uniq.length - 1, 1) };
  });

  const targetVals = rows.map((r) => r[target]).filter((v) => v !== '' && v != null);
  let classList = [];
  let classMap = null;
  if (task === 'classification') {
    classList = [...new Set(targetVals.map((v) => normText(v)))];
    classMap = Object.fromEntries(classList.map((v, idx) => [v, idx]));
  }

  const X = [];
  const Y = [];
  const validRows = [];
  rows.forEach((row) => {
    const x = [];
    for (const col of features) {
      const val = row[col];
      if (typeMap[col] === 'numeric') {
        const n = parseFloat(val);
        x.push(Number.isFinite(n) ? n : 0);
      } else {
        const token = normText(val);
        const enc = colEncoders[col];
        const idx = enc.map[token] ?? 0;
        x.push(idx / enc.scale);
      }
    }
    if (task === 'regression') {
      const y = parseFloat(row[target]);
      if (!Number.isFinite(y)) return;
      X.push(x);
      Y.push(y);
      validRows.push(row);
      return;
    }

    const label = normText(row[target]);
    if (!label || classMap[label] == null) return;
    X.push(x);
    Y.push(classMap[label]);
    validRows.push(row);
  });

  if (X.length < 20) return null;

  const split = clamp(featureCfg.split ?? 0.2, 0.1, 0.4);
  const indices = Array.from({ length: X.length }, (_, i) => i);
  for (let i = indices.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }

  const trainSize = Math.max(8, Math.floor(X.length * (1 - split)));
  const trainIdx = indices.slice(0, trainSize);
  const testIdx = indices.slice(trainSize);

  const XTrain = trainIdx.map((idx) => [...X[idx]]);
  const YTrain = trainIdx.map((idx) => Y[idx]);
  const XTest = testIdx.map((idx) => [...X[idx]]);
  const YTest = testIdx.map((idx) => Y[idx]);

  let mins = null;
  let maxs = null;
  let means = null;
  let stds = null;

  if (featureCfg.scaling !== 'none') {
    const d = XTrain[0].length;
    mins = Array(d).fill(Infinity);
    maxs = Array(d).fill(-Infinity);
    means = Array(d).fill(0);
    stds = Array(d).fill(0);
    XTrain.forEach((x) => {
      for (let j = 0; j < d; j += 1) {
        mins[j] = Math.min(mins[j], x[j]);
        maxs[j] = Math.max(maxs[j], x[j]);
        means[j] += x[j];
      }
    });
    for (let j = 0; j < d; j += 1) means[j] /= Math.max(XTrain.length, 1);
    XTrain.forEach((x) => {
      for (let j = 0; j < d; j += 1) {
        stds[j] += (x[j] - means[j]) ** 2;
      }
    });
    for (let j = 0; j < d; j += 1) stds[j] = Math.sqrt(stds[j] / Math.max(XTrain.length, 1));

    const scaleOne = (x) => {
      for (let j = 0; j < d; j += 1) {
        if (featureCfg.scaling === 'minmax') {
          const den = Math.max(maxs[j] - mins[j], 1e-9);
          x[j] = (x[j] - mins[j]) / den;
        } else {
          x[j] = (x[j] - means[j]) / Math.max(stds[j], 1e-9);
        }
      }
    };

    XTrain.forEach(scaleOne);
    XTest.forEach(scaleOne);
  }

  return {
    task,
    features,
    target,
    inputSize: features.length,
    outputSize: task === 'classification' ? Math.max(classList.length, 2) : 1,
    classList,
    XTrain,
    YTrain,
    XTest,
    YTest,
    validRows,
    rowsUsed: X.length,
    split,
    preprocessing: {
      typeMap,
      colEncoders,
      scaling: featureCfg.scaling,
      mins,
      maxs,
      means,
      stds,
    },
  };
}

function act(name, x) {
  if (name === 'gelu') {
    return 0.5 * x * (1 + Math.tanh(Math.sqrt(2 / Math.PI) * (x + 0.044715 * x * x * x)));
  }
  if (name === 'relu') return Math.max(0, x);
  if (name === 'tanh') return Math.tanh(x);
  if (name === 'sigmoid') return 1 / (1 + Math.exp(-x));
  if (name === 'leaky_relu') return x > 0 ? x : 0.01 * x;
  if (name === 'swish') return x / (1 + Math.exp(-x));
  return x;
}

function actDeriv(name, x) {
  if (name === 'gelu') {
    const u = Math.sqrt(2 / Math.PI) * (x + 0.044715 * x * x * x);
    const tanhU = Math.tanh(u);
    const sech2 = 1 - tanhU * tanhU;
    return 0.5 * (1 + tanhU) + 0.5 * x * sech2 * Math.sqrt(2 / Math.PI) * (1 + 3 * 0.044715 * x * x);
  }
  if (name === 'relu') return x > 0 ? 1 : 0;
  if (name === 'tanh') {
    const t = Math.tanh(x);
    return 1 - t * t;
  }
  if (name === 'sigmoid') {
    const s = 1 / (1 + Math.exp(-x));
    return s * (1 - s);
  }
  if (name === 'leaky_relu') return x > 0 ? 1 : 0.01;
  if (name === 'swish') {
    const s = 1 / (1 + Math.exp(-x));
    return s + x * s * (1 - s);
  }
  return 1;
}

function softmax(logits) {
  const m = Math.max(...logits);
  const ex = logits.map((x) => Math.exp(x - m));
  const s = ex.reduce((a, b) => a + b, 0);
  return ex.map((v) => v / Math.max(s, 1e-9));
}

function makeMatrix(rows, cols, fn) {
  const out = Array(rows);
  for (let r = 0; r < rows; r += 1) {
    out[r] = Array(cols);
    for (let c = 0; c < cols; c += 1) out[r][c] = fn(r, c);
  }
  return out;
}

function cloneGradLike(weights, biases) {
  return {
    dW: weights.map((w) => w.map((row) => row.map(() => 0))),
    dB: biases.map((b) => b.map(() => 0)),
  };
}

function initParams(layerSizes, initializer) {
  const weights = [];
  const biases = [];
  for (let l = 0; l < layerSizes.length - 1; l += 1) {
    const inN = layerSizes[l];
    const outN = layerSizes[l + 1];
    const w = makeMatrix(outN, inN, () => {
      if (initializer === 'zeros') return 0;
      if (initializer === 'normal') return randomBetween(-0.05, 0.05);
      if (initializer === 'he') {
        const bound = Math.sqrt(6 / inN);
        return randomBetween(-bound, bound);
      }
      if (initializer === 'uniform') {
        const bound = 1 / Math.sqrt(Math.max(inN, 1));
        return randomBetween(-bound, bound);
      }
      const xavierBound = Math.sqrt(6 / Math.max(inN + outN, 1));
      return randomBetween(-xavierBound, xavierBound);
    });
    const b = Array(outN).fill(0);
    weights.push(w);
    biases.push(b);
  }
  return { weights, biases };
}

function forwardPass(model, input, task, hiddenActivation) {
  const { weights, biases } = model;
  const activations = [input];
  const zs = [];
  let a = input;

  for (let l = 0; l < weights.length; l += 1) {
    const out = weights[l].length;
    const z = Array(out).fill(0);
    for (let j = 0; j < out; j += 1) {
      let s = biases[l][j];
      const wj = weights[l][j];
      for (let k = 0; k < wj.length; k += 1) s += wj[k] * a[k];
      z[j] = s;
    }
    zs.push(z);

    const last = l === weights.length - 1;
    if (!last) {
      const actName = Array.isArray(hiddenActivation)
        ? (hiddenActivation[l] || 'relu')
        : hiddenActivation;
      a = z.map((v) => act(actName, v));
    } else if (task === 'classification') {
      a = softmax(z);
    } else {
      a = [z[0]];
    }
    activations.push(a);
  }
  return { activations, zs, output: activations[activations.length - 1] };
}

function backwardPass(model, cache, y, task, hiddenActivation) {
  const { weights } = model;
  const { activations, zs, output } = cache;
  const L = weights.length;
  const grads = cloneGradLike(model.weights, model.biases);
  let delta = [];
  let loss = 0;
  let correct = 0;

  if (task === 'classification') {
    delta = [...output];
    delta[y] -= 1;
    loss = -Math.log(Math.max(output[y], 1e-9));
    const pred = output.indexOf(Math.max(...output));
    correct = pred === y ? 1 : 0;
  } else {
    const err = output[0] - y;
    delta = [err];
    loss = 0.5 * err * err;
  }

  for (let l = L - 1; l >= 0; l -= 1) {
    const aPrev = activations[l];
    for (let j = 0; j < delta.length; j += 1) {
      grads.dB[l][j] += delta[j];
      for (let k = 0; k < aPrev.length; k += 1) {
        grads.dW[l][j][k] += delta[j] * aPrev[k];
      }
    }

    if (l > 0) {
      const prev = Array(weights[l][0].length).fill(0);
      for (let k = 0; k < prev.length; k += 1) {
        let s = 0;
        for (let j = 0; j < delta.length; j += 1) s += weights[l][j][k] * delta[j];
        const actName = Array.isArray(hiddenActivation)
          ? (hiddenActivation[l - 1] || 'relu')
          : hiddenActivation;
        prev[k] = s * actDeriv(actName, zs[l - 1][k]);
      }
      delta = prev;
    }
  }

  const gradNorm = Math.sqrt(
    grads.dW
      .flat(2)
      .reduce((sum, v) => sum + v * v, 0)
  );

  return { grads, loss, correct, gradNorm };
}

function applyOptimizer(model, grads, batchSize, cfg, state) {
  const lr = cfg.useCustomLr ? cfg.lr : 0.01;
  const opt = resolveOptimizerFamily(cfg.optimizer);
  const eps = 1e-8;
  const beta1 = 0.9;
  const beta2 = 0.999;
  const decay = 0.99;
  state.t += 1;

  for (let l = 0; l < model.weights.length; l += 1) {
    for (let j = 0; j < model.weights[l].length; j += 1) {
      let gb = grads.dB[l][j] / batchSize;
      if (opt === 'adam') {
        state.mB[l][j] = beta1 * state.mB[l][j] + (1 - beta1) * gb;
        state.vB[l][j] = beta2 * state.vB[l][j] + (1 - beta2) * gb * gb;
        const mh = state.mB[l][j] / (1 - beta1 ** state.t);
        const vh = state.vB[l][j] / (1 - beta2 ** state.t);
        gb = mh / (Math.sqrt(vh) + eps);
      } else if (opt === 'momentum') {
        state.vB[l][j] = cfg.momentum * state.vB[l][j] + gb;
        gb = state.vB[l][j];
      } else if (opt === 'rmsprop') {
        state.vB[l][j] = decay * state.vB[l][j] + (1 - decay) * gb * gb;
        gb = gb / (Math.sqrt(state.vB[l][j]) + eps);
      }
      model.biases[l][j] -= lr * gb;

      for (let k = 0; k < model.weights[l][j].length; k += 1) {
        let gw = grads.dW[l][j][k] / batchSize;
        if (opt === 'adam') {
          state.mW[l][j][k] = beta1 * state.mW[l][j][k] + (1 - beta1) * gw;
          state.vW[l][j][k] = beta2 * state.vW[l][j][k] + (1 - beta2) * gw * gw;
          const mh = state.mW[l][j][k] / (1 - beta1 ** state.t);
          const vh = state.vW[l][j][k] / (1 - beta2 ** state.t);
          gw = mh / (Math.sqrt(vh) + eps);
        } else if (opt === 'momentum') {
          state.vW[l][j][k] = cfg.momentum * state.vW[l][j][k] + gw;
          gw = state.vW[l][j][k];
        } else if (opt === 'rmsprop') {
          state.vW[l][j][k] = decay * state.vW[l][j][k] + (1 - decay) * gw * gw;
          gw = gw / (Math.sqrt(state.vW[l][j][k]) + eps);
        }
        model.weights[l][j][k] -= lr * gw;
      }
    }
  }
}

function evaluateModel(model, data, hiddenActivation) {
  if (!model || !data) return null;
  let loss = 0;
  let correct = 0;
  const preds = [];
  for (let i = 0; i < data.XTest.length; i += 1) {
    const out = forwardPass(model, data.XTest[i], data.task, hiddenActivation).output;
    if (data.task === 'classification') {
      const pred = out.indexOf(Math.max(...out));
      preds.push(pred);
      if (pred === data.YTest[i]) correct += 1;
      loss += -Math.log(Math.max(out[data.YTest[i]], 1e-9));
    } else {
      const err = out[0] - data.YTest[i];
      preds.push(out[0]);
      loss += 0.5 * err * err;
    }
  }

  const n = Math.max(data.XTest.length, 1);
  const result = {
    testLoss: loss / n,
    preds,
  };

  if (data.task === 'classification') {
    result.testAcc = correct / n;
    const k = data.outputSize;
    const matrix = Array.from({ length: k }, () => Array(k).fill(0));
    data.YTest.forEach((trueY, idx) => {
      const pred = preds[idx];
      matrix[trueY][pred] += 1;
    });
    result.confusion = matrix;
  } else {
    const mean = data.YTest.reduce((a, b) => a + b, 0) / n;
    const ssRes = preds.reduce((s, p, i) => s + (data.YTest[i] - p) ** 2, 0);
    const ssTot = data.YTest.reduce((s, y) => s + (y - mean) ** 2, 0);
    result.rmse = Math.sqrt(ssRes / n);
    result.mae = preds.reduce((s, p, i) => s + Math.abs(data.YTest[i] - p), 0) / n;
    result.r2 = 1 - ssRes / Math.max(ssTot, 1e-9);
  }

  return result;
}

function classificationFromConfusion(confusion) {
  if (!confusion?.length) {
    return {
      accuracy: 0,
      precision: 0,
      recall: 0,
      f1: 0,
      report: [],
    };
  }

  const classes = confusion.length;
  const report = [];
  let macroP = 0;
  let macroR = 0;
  let macroF1 = 0;
  let total = 0;
  let correct = 0;

  for (let i = 0; i < classes; i += 1) {
    const tp = confusion[i][i];
    let fp = 0;
    let fn = 0;
    let support = 0;

    for (let r = 0; r < classes; r += 1) {
      support += confusion[i][r];
      if (r !== i) fn += confusion[i][r];
    }
    for (let r = 0; r < classes; r += 1) {
      if (r !== i) fp += confusion[r][i];
    }

    const precision = tp / Math.max(tp + fp, 1);
    const recall = tp / Math.max(tp + fn, 1);
    const f1 = (2 * precision * recall) / Math.max(precision + recall, 1e-9);

    macroP += precision;
    macroR += recall;
    macroF1 += f1;
    total += support;
    correct += tp;

    report.push({
      classIndex: i,
      precision,
      recall,
      f1,
      support,
    });
  }

  return {
    accuracy: correct / Math.max(total, 1),
    precision: macroP / classes,
    recall: macroR / classes,
    f1: macroF1 / classes,
    report,
  };
}

function evaluateSplit(model, task, hiddenActivation, X, Y, outputSize) {
  const n = Math.max(X?.length || 0, 1);
  if (!X?.length || !Y?.length) {
    return {
      loss: 0,
      acc: null,
      precision: null,
      recall: null,
      f1: null,
      confusion: null,
      report: [],
    };
  }

  let loss = 0;
  let correct = 0;
  const preds = [];
  if (task === 'classification') {
    const k = Math.max(outputSize || 2, 2);
    const confusion = Array.from({ length: k }, () => Array(k).fill(0));
    for (let i = 0; i < X.length; i += 1) {
      const out = forwardPass(model, X[i], task, hiddenActivation).output;
      const pred = out.indexOf(Math.max(...out));
      preds.push(pred);
      if (pred === Y[i]) correct += 1;
      confusion[Y[i]][pred] += 1;
      loss += -Math.log(Math.max(out[Y[i]], 1e-9));
    }
    const cls = classificationFromConfusion(confusion);
    return {
      loss: loss / n,
      acc: cls.accuracy,
      precision: cls.precision,
      recall: cls.recall,
      f1: cls.f1,
      confusion,
      report: cls.report,
      preds,
    };
  }

  for (let i = 0; i < X.length; i += 1) {
    const out = forwardPass(model, X[i], task, hiddenActivation).output;
    const err = out[0] - Y[i];
    preds.push(out[0]);
    loss += 0.5 * err * err;
  }

  return {
    loss: loss / n,
    acc: null,
    precision: null,
    recall: null,
    f1: null,
    confusion: null,
    report: [],
    preds,
  };
}

function encodeRowsForPrediction(rows, prepared) {
  if (!rows?.length || !prepared?.features?.length || !prepared?.preprocessing) return [];
  const { features, preprocessing } = prepared;
  const { typeMap, colEncoders, scaling, mins, maxs, means, stds } = preprocessing;

  return rows.map((row) => {
    const x = features.map((col, index) => {
      if (typeMap?.[col] === 'numeric') {
        const n = parseFloat(row[col]);
        return Number.isFinite(n) ? n : 0;
      }
      const token = normText(row[col]);
      const enc = colEncoders?.[col];
      const raw = enc?.map?.[token] ?? 0;
      return raw / Math.max(enc?.scale || 1, 1);
    });

    if (scaling === 'minmax' && mins && maxs) {
      return x.map((value, idx) => (value - mins[idx]) / Math.max((maxs[idx] - mins[idx]) || 0, 1e-9));
    }
    if (scaling === 'zscore' && means && stds) {
      return x.map((value, idx) => (value - means[idx]) / Math.max(stds[idx] || 0, 1e-9));
    }
    return x;
  });
}

function runPredictions(model, prepared, hiddenActs, vectors) {
  if (!model || !prepared || !vectors?.length) return [];
  const rows = [];
  for (let i = 0; i < vectors.length; i += 1) {
    const out = forwardPass(model, vectors[i], prepared.task, hiddenActs).output;
    if (prepared.task === 'classification') {
      const predIdx = out.indexOf(Math.max(...out));
      rows.push({
        index: i,
        prediction: prepared.classList[predIdx] ?? `Class ${predIdx}`,
        confidence: out[predIdx],
        scores: out,
      });
    } else {
      rows.push({
        index: i,
        prediction: out[0],
        confidence: null,
        scores: [out[0]],
      });
    }
  }
  return rows;
}

function TopologyDiagram({ layerSizes, activation, activations }) {
  const width = 540;
  const height = 340;
  const maxDraw = 8;
  const gap = width / (layerSizes.length + 1);

  const nodes = layerSizes.map((size, li) => {
    const draw = Math.min(size, maxDraw);
    const yGap = height / (draw + 1);
    return Array.from({ length: draw }, (_, ni) => ({
      x: gap * (li + 1),
      y: yGap * (ni + 1),
      li,
      ni,
      omitted: size > maxDraw && ni === draw - 1,
    }));
  });

  const getActVal = (li, ni) => {
    if (!activations || !activations[li]) return null;
    const v = activations[li][ni];
    return v !== undefined ? v : null;
  };

  const allActsFlat = activations ? activations.flat().filter(v => v !== undefined && !isNaN(v)) : [];
  const maxAbs = allActsFlat.length ? Math.max(...allActsFlat.map(Math.abs), 0.001) : 1;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 350, border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}>
      {nodes.slice(0, -1).map((layer, li) => (
        <g key={`edges-${li}`}>
          {layer.map((a) => nodes[li + 1].map((b) => {
            const av = getActVal(li, a.ni);
            const opacity = av !== null ? 0.05 + Math.abs(av) / maxAbs * 0.95 : 0.28;
            const stroke = av !== null && av > 0 ? C.cyan : av !== null && av < 0 ? C.red : C.b2;
            return (
              <line key={`${a.li}-${a.ni}-${b.li}-${b.ni}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeOpacity={opacity} strokeWidth="1" />
            );
          }))}
        </g>
      ))}

      {nodes.map((layer, li) => {
        const col = li === 0 ? C.cyan : li === nodes.length - 1 ? C.green : C.violet;
        return (
          <g key={`nodes-${li}`}>
            {layer.map((node) => {
              const av = getActVal(node.li, node.ni);
              const norm = av !== null ? Math.abs(av) / maxAbs : 0;
              const isDead = av !== null && av === 0;
              const nodeColor = av !== null
                ? (isDead ? C.b1 : av > 0 ? C.cyan : C.red)
                : col;

              return (
                <g key={`${node.li}-${node.ni}`}>
                  {av !== null && norm > 0.1 && (
                    <circle cx={node.x} cy={node.y} r={12 + norm * 6} fill="none"
                      stroke={av > 0 ? C.cyan : C.red}
                      strokeWidth={1} opacity={norm * 0.5} />
                  )}
                  <circle cx={node.x} cy={node.y} r="7" fill={av !== null ? `${nodeColor}${Math.round(20 + norm * 200).toString(16).padStart(2, '0')}` : `${col}22`} stroke={nodeColor} strokeWidth="1.2" />
                  {av !== null && (
                    <text x={node.x} y={node.y + 3} textAnchor="middle" fontSize="9" fill={norm > 0.5 ? C.void : nodeColor} fontFamily={mono}>
                      {isDead ? '0' : av.toFixed(1)}
                    </text>
                  )}
                  {node.omitted && (
                    <text x={node.x} y={node.y + 3} textAnchor="middle" fontSize="9" fill={C.t3} fontFamily={mono}>…</text>
                  )}
                </g>
              );
            })}
            <text x={layer[0]?.x || 0} y={16} textAnchor="middle" fontSize="9" fill={col} fontFamily={mono}>{layerSizes[li]}</text>
          </g>
        );
      })}

      <text x="14" y="325" fontSize="9" fill={C.t4} fontFamily={mono}>Activation: {activation.toUpperCase()}</text>
      <text x="430" y="325" fontSize="9" fill={C.t4} fontFamily={mono}>ANN builder</text>
    </svg>
  );
}

function WeightHeatmap({ model }) {
  const layer = model?.weights?.[0] || [];
  if (!layer.length) return <div style={{ color: C.t4, fontSize: 10 }}>No weights yet.</div>;
  const flat = layer.flat();
  const maxAbs = Math.max(...flat.map((v) => Math.abs(v)), 1e-9);
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${layer[0].length}, minmax(12px, 1fr))`, gap: 2 }}>
        {flat.map((w, idx) => {
          const p = Math.abs(w) / maxAbs;
          const color = w >= 0 ? `rgba(34,211,238,${0.15 + p * 0.8})` : `rgba(248,113,113,${0.15 + p * 0.8})`;
          return <div key={idx} title={fnum(w, 5)} style={{ height: 12, background: color, borderRadius: 2, border: `1px solid ${C.b0}` }} />;
        })}
      </div>

      <div style={{ display: 'grid', gap: 4 }}>
        <div
          style={{
            height: 10,
            borderRadius: 999,
            border: `1px solid ${C.b1}`,
            background: 'linear-gradient(90deg, rgba(248,113,113,0.95) 0%, rgba(248,113,113,0.2) 42%, rgba(148,163,184,0.35) 50%, rgba(34,211,238,0.2) 58%, rgba(34,211,238,0.95) 100%)',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: C.t3, fontFamily: mono }}>
          <span>{`NEG (${fnum(-maxAbs, 3)})`}</span>
          <span>0</span>
          <span>{`POS (${fnum(maxAbs, 3)})`}</span>
        </div>
        <div style={{ fontSize: 9, color: C.t4, fontFamily: mono }}>Red = negative weights · Cyan = positive weights · Darker = larger magnitude</div>
      </div>
    </div>
  );
}

function DataSourceTab({ onLoaded, autoUrl, autoCsv, onAutoConsumed }) {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState('file');
  const [showHF, setShowHF] = useState(false);
  const [isDrag, setIsDrag] = useState(false);
  const fileRef = useRef();

  const parseAndLoad = useCallback(async (text, name, sizeBytes = 0) => {
    await new Promise(r => setTimeout(r, 0)); // yield — let browser paint spinner
    const ds = parseCSV(text);
    if (!ds) {
      setStatus('Failed to parse dataset. CSV format required.');
      setLoading(false);
      return;
    }
    onLoaded(ds, name || 'dataset.csv');
    setStatus(`${name || 'dataset.csv'} · ${ds.rows.length.toLocaleString()} rows · ${ds.headers.length} cols${sizeBytes ? ' · ' + fmtBytes(sizeBytes) : ''}`);
    setLoading(false);
  }, [onLoaded]);

  const onFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setStatus(`Reading ${file.name}…`);
    const txt = await file.text();
    parseAndLoad(txt, file.name, file.size);
  };

  const loadUrl = async () => {
    try {
      if (!String(url || '').trim()) {
        setStatus('Enter a URL');
        return;
      }
      setLoading(true);
      setStatus('Loading URL...');
      const res = await fetch(url);
      const txt = await res.text();
      parseAndLoad(txt, url.split('/').pop() || 'remote.csv', txt.length);
    } catch {
      setStatus('Unable to fetch URL');
      setLoading(false);
    }
  };

  const loadSample = async (item) => {
    try {
      setLoading(true);
      setStatus(`Loading ${item.name}...`);
      const res = await fetch(item.url);
      const txt = await res.text();
      parseAndLoad(txt, item.name, txt.length);
    } catch {
      setStatus(`Unable to load ${item.name}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoUrl || loading) return;
    setUrl(String(autoUrl));
    setStatus('Loading URL...');
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(String(autoUrl).trim());
        const txt = await res.text();
        parseAndLoad(txt, String(autoUrl).split('/').pop() || 'remote.csv', txt.length);
      } catch {
        setStatus('Unable to fetch URL');
        setLoading(false);
      } finally {
        onAutoConsumed?.();
      }
    })();
  }, [autoUrl, loading, onAutoConsumed, parseAndLoad]);

  useEffect(() => {
    if (!autoCsv?.csvText || loading) return;
    setLoading(true);
    parseAndLoad(String(autoCsv.csvText), autoCsv.name || 'dataset.csv');
    onAutoConsumed?.();
  }, [autoCsv?.id, autoCsv?.csvText, autoCsv?.name, loading, onAutoConsumed, parseAndLoad]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 40px 32px', minHeight: '100%', position: 'relative' }}>
      <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 300, background: `radial-gradient(ellipse,${C.orange}08 0%,transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ textAlign: 'center', marginBottom: 40, position: 'relative' }}>
        <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 38, color: C.t1, lineHeight: 1.1, margin: '0 0 12px' }}>
          Build, Train, Visualize
          <br />
          <span style={{
            background: `linear-gradient(100deg, ${C.green} 0%, ${C.cyan} 45%, ${C.violet} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: `0 0 18px ${C.violet}33`,
            letterSpacing: '.01em',
          }}>
            Neural Networks
          </span>
        </h1>
        <p style={{ fontSize: 12, color: C.t3, maxWidth: 700, lineHeight: 1.85, margin: '0 auto' }}>
          ANNs · CNNs · RNNs · LSTMs · GRUs · Pretrained · Live Visualization
          <br></br>No setup required, Just mouse clicks.
        </p>

        <span style={{ display:'inline-block', marginTop:6, padding:'4px 10px', borderRadius:999, background:`linear-gradient(90deg, ${C.cyan}18, ${C.violet}16)`, border:`1px solid ${C.cyan}33`, color:C.t1 }}>
          Your data never leaves your machine. 100% on Browser & 100% Privacy.
        </span>
      </div>

      <div style={{ width: '100%', maxWidth: 720, background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 8, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,.6)', position: 'relative', marginBottom: 28 }}>
        <div style={{ display: 'flex', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
          {[['file', '📁 Upload File'], ['hf', '🤗 HuggingFace'], ['url', '🌐 From URL'], ['sample', '⬟ Sample Datasets']].map(([id, label]) => (
            <button key={id} onClick={() => { if (id === 'hf') { setShowHF(true); return; } setSrc(id); setStatus(''); }} style={{ flex: 1, padding: '14px', border: 'none', background: (id === 'hf' ? showHF : src === id) ? C.surface : 'transparent', color: (id === 'hf' ? showHF : src === id) ? C.cyan : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, cursor: 'pointer', borderBottom: `2px solid ${(id === 'hf' ? showHF : src === id) ? C.cyan : 'transparent'}`, transition: 'all .12s' }}>{label}</button>
          ))}
        </div>
        <div style={{ padding: 30 }}>
          {src === 'file' && (
            <div
              onDragOver={(event) => { event.preventDefault(); setIsDrag(true); }}
              onDragLeave={() => setIsDrag(false)}
              onDrop={(event) => { event.preventDefault(); setIsDrag(false); onFile(event.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${isDrag ? C.cyan : C.b2}`, borderRadius: 8, padding: '60px 20px', textAlign: 'center', cursor: loading ? 'wait' : 'pointer', background: isDrag ? `${C.cyan}07` : C.deep, transition: 'all .2s' }}
            >
              <div style={{ fontSize: 52, marginBottom: 12, opacity: 0.5 }}>{loading ? '⏳' : '📊'}</div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 17, color: C.t1, marginBottom: 6 }}>{loading ? 'Parsing...' : 'Drop your CSV here'}</div>
              <div style={{ fontSize: 11, color: C.t3 }}>{loading ? 'Building full analysis…' : 'or click to browse · CSV / TSV / TXT'}</div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            </div>
          )}
          {src === 'url' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>PUBLIC CSV / TSV URL</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com/data.csv" onKeyDown={(event) => event.key === 'Enter' && loadUrl()} style={{ flex: 1, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '12px 14px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 4, outline: 'none' }} />
                <button onClick={loadUrl} disabled={!url || loading} style={{ padding: '12px 22px', background: `${C.cyan}18`, border: `1px solid ${C.cyan}`, color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: 'pointer' }}>{loading ? '…' : 'FETCH'}</button>
              </div>
            </div>
          )}
          {src === 'sample' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
              {SAMPLE_DATASETS.map((sample) => (
                <button key={sample.name} onClick={() => loadSample(sample)} disabled={loading} style={{ padding: '16px', background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 6, cursor: 'pointer', textAlign: 'left', transition: 'border-color .15s', opacity: loading ? 0.6 : 1 }} onMouseEnter={(event) => { event.currentTarget.style.borderColor = C.cyan; }} onMouseLeave={(event) => { event.currentTarget.style.borderColor = C.b1; }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>⬟</div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, fontSize: 11, color: C.t1, marginBottom: 3 }}>{sample.name}</div>
                  <div style={{ fontSize: 8.5, color: C.t4, lineHeight: 1.5 }}>{sample.desc}</div>
                </button>
              ))}
            </div>
          )}
          {showHF && (
            <HFDatasetPicker
              accentColor={C.cyan}
              onClose={() => setShowHF(false)}
              onLoaded={(csv, name, size) => parseAndLoad(csv, name, size)}
            />
          )}

          {status && <div style={{ marginTop: 12, padding: '9px 14px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 4, fontSize: 10, color: C.red }}>{status}</div>}
        </div>
      </div>
    </div>
  );
}

function FeatureTab({ ds, cfg, setCfg, prepared }) {
  const types = useMemo(() => inferTypes(ds.headers, ds.rows), [ds]);
  const typeMap = useMemo(() => Object.fromEntries(types.map((t) => [t.col, t.type])), [types]);
  const numCols = useMemo(() => types.filter((t) => t.type === 'numeric').map((t) => t.col), [types]);
  const catCols = useMemo(() => types.filter((t) => t.type === 'categorical').map((t) => t.col), [types]);

  const FE_SUBTABS = [
    { id: 'target', label: 'TARGET', icon: '🎯', color: C.orange },
    { id: 'encoding', label: 'ENCODING', icon: '🔤', color: C.violet },
    { id: 'scaling', label: 'SCALING', icon: '📏', color: C.cyan },
    { id: 'analysis', label: 'FEATURE ANALYSIS', icon: '📊', color: C.green },
    { id: 'pca', label: 'PCA', icon: '⬡', color: C.violet },
  ];

  const [sub, setSub] = useState('target');
  const [previewMode, setPreviewMode] = useState('head');
  const [previewN, setPreviewN] = useState(5);
  const [encodingMethod, setEncodingMethod] = useState('label');
  const [pcaK, setPcaK] = useState(2);

  const setTarget = (target) => {
    const features = ds.headers.filter((h) => h !== target);
    setCfg((p) => ({ ...p, target, features, task: inferTask(ds, target) }));
  };

  const toggleFeature = (column) => {
    if (column === cfg.target) return;
    setCfg((prev) => {
      const exists = prev.features.includes(column);
      const features = exists ? prev.features.filter((f) => f !== column) : [...prev.features, column];
      return { ...prev, features };
    });
  };

  const targetInfo = useMemo(() => {
    const t = types.find((x) => x.col === cfg.target);
    if (!t) return null;
    return {
      unique: t.uniqueCount,
      type: t.type,
      nullPct: +(((t.nullCount || 0) / Math.max(ds.rows.length, 1)) * 100).toFixed(1),
      task: (cfg.task === 'auto' ? inferTask(ds, cfg.target) : cfg.task).toUpperCase(),
    };
  }, [types, cfg.target, cfg.task, ds]);

  const previewRows = useMemo(() => {
    const rows = ds.rows || [];
    if (previewMode === 'tail') return rows.slice(-previewN);
    if (previewMode === 'sample') {
      const copy = [...rows];
      for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy.slice(0, previewN);
    }
    return rows.slice(0, previewN);
  }, [ds.rows, previewMode, previewN]);

  const previewHeaders = useMemo(() => {
    const cols = [];
    if (cfg.target) cols.push(cfg.target);
    cfg.features.forEach((f) => {
      if (!cols.includes(f)) cols.push(f);
    });
    return cols;
  }, [cfg.target, cfg.features]);

  const featImportance = useMemo(() => {
    if (!cfg.target || !cfg.features.length) return [];
    return cfg.features.map((col) => {
      if (typeMap[col] === 'numeric' && typeMap[cfg.target] === 'numeric') {
        return { col, score: Math.abs(pearson(ds.rows, col, cfg.target)) || 0 };
      }
      const uniq = new Set(ds.rows.map((r) => r[col]).filter((v) => v !== '' && v != null));
      return { col, score: Math.min(1, uniq.size / Math.max(ds.rows.length * 0.25, 1)) };
    }).sort((a, b) => b.score - a.score);
  }, [cfg.target, cfg.features, ds.rows, typeMap]);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {FE_SUBTABS.map((item) => (
          <button
            key={item.id}
            onClick={() => setSub(item.id)}
            style={{
              padding: '5px 10px',
              border: `1px solid ${sub === item.id ? item.color : C.b1}`,
              background: sub === item.id ? `${item.color}18` : 'transparent',
              color: sub === item.id ? item.color : C.t3,
              fontFamily: mono,
              fontSize: 9,
              fontWeight: 700,
              borderRadius: 2,
              cursor: 'pointer',
              letterSpacing: '.08em',
            }}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      {sub === 'target' && (
        <Card>
          <CardHead label="SELECT TARGET COLUMN" accent={C.orange} />
          <div style={{ padding: 14, display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,320px) 1fr', gap: 10, alignItems: 'end' }}>
              <Sel
                label="TARGET COLUMN (what to predict)"
                value={cfg.target}
                onChange={setTarget}
                options={ds.headers.map((h) => ({ value: h, label: h }))}
              />
              {targetInfo && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <MetricTile label="TASK TYPE" value={targetInfo.task} color={targetInfo.task === 'REGRESSION' ? C.cyan : C.green} />
                  <MetricTile label="UNIQUE VALUES" value={targetInfo.unique} color={C.violet} />
                  <MetricTile label="DATA TYPE" value={targetInfo.type} color={C.gold} />
                  <MetricTile label="NULL %" value={`${targetInfo.nullPct}%`} color={C.orange} />
                </div>
              )}
            </div>

            <div>
              <div style={{ fontFamily: mono, fontSize: 9, color: C.t4, marginBottom: 6, letterSpacing: '.12em' }}>FEATURE COLUMNS — click to toggle on/off</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ds.headers.filter((h) => h !== cfg.target).map((h) => {
                  const on = cfg.features.includes(h);
                  const c = typeMap[h] === 'numeric' ? C.cyan : C.gold;
                  return (
                    <button
                      key={h}
                      onClick={() => toggleFeature(h)}
                      style={{
                        padding: '5px 10px',
                        border: `1px solid ${on ? c : C.b1}`,
                        background: on ? `${c}18` : 'transparent',
                        color: on ? c : C.t3,
                        fontSize: 10,
                        borderRadius: 3,
                        cursor: 'pointer',
                        fontFamily: mono,
                      }}
                    >
                      {on ? '✓ ' : ''}{h} <span style={{ opacity: 0.7 }}>{typeMap[h] === 'numeric' ? 'n' : 'c'}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: C.t3, marginTop: 8 }}>
                {cfg.features.length} features · {ds.rows.length} rows · {cfg.features.filter((f) => typeMap[f] === 'numeric').length} numeric · {cfg.features.filter((f) => typeMap[f] !== 'numeric').length} categorical
              </div>
            </div>
          </div>
        </Card>
      )}

      {sub === 'encoding' && (
        <Card>
          <CardHead label="ENCODING" accent={C.violet} />
          <div style={{ padding: 14, display: 'grid', gap: 12 }}>
            <Sel
              label="ENCODING METHOD"
              value={encodingMethod}
              onChange={setEncodingMethod}
              options={[
                { value: 'label', label: 'LABEL' },
                { value: 'onehot', label: 'ONE-HOT' },
              ]}
            />
            <div style={{ fontSize: 10, color: C.t3 }}>
              Selected categorical features: {cfg.features.filter((f) => typeMap[f] !== 'numeric').length} · method: {encodingMethod.toUpperCase()}
            </div>
          </div>
        </Card>
      )}

      {sub === 'scaling' && (
        <Card>
          <CardHead label="SCALING" accent={C.cyan} />
          <div style={{ padding: 14, display: 'grid', gridTemplateColumns: 'repeat(3,minmax(120px,1fr))', gap: 10 }}>
            <Sel
              label="SCALING"
              value={cfg.scaling}
              onChange={(scaling) => setCfg((p) => ({ ...p, scaling }))}
              options={[
                { value: 'zscore', label: 'Z-SCORE' },
                { value: 'minmax', label: 'MIN-MAX' },
                { value: 'none', label: 'NONE' },
              ]}
            />
            <Sel
              label="TASK TYPE"
              value={cfg.task}
              onChange={(task) => setCfg((p) => ({ ...p, task }))}
              options={[
                { value: 'auto', label: 'AUTO' },
                { value: 'classification', label: 'CLASSIFICATION' },
                { value: 'regression', label: 'REGRESSION' },
              ]}
            />
            <Sel
              label="TEST SPLIT"
              value={String(cfg.split)}
              onChange={(v) => setCfg((p) => ({ ...p, split: parseFloat(v) }))}
              options={[
                { value: '0.15', label: '15%' },
                { value: '0.2', label: '20%' },
                { value: '0.25', label: '25%' },
                { value: '0.3', label: '30%' },
              ]}
            />
          </div>
        </Card>
      )}

      {sub === 'analysis' && (
        <Card>
          <CardHead label="FEATURE ANALYSIS" accent={C.green} />
          <div style={{ padding: 14 }}>
            {featImportance.length ? (
              <div style={{ display: 'grid', gap: 6 }}>
                {featImportance.slice(0, 12).map((item) => (
                  <div key={item.col} style={{ display: 'grid', gridTemplateColumns: '170px 1fr 55px', gap: 8, alignItems: 'center' }}>
                    <div style={{ fontFamily: mono, fontSize: 10, color: C.t2 }}>{item.col}</div>
                    <div style={{ height: 6, borderRadius: 3, background: C.b1 }}>
                      <div style={{ width: `${Math.min(100, item.score * 100)}%`, height: '100%', borderRadius: 3, background: C.green }} />
                    </div>
                    <div style={{ fontFamily: mono, fontSize: 10, color: C.green, textAlign: 'right' }}>{item.score.toFixed(3)}</div>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: C.t4, fontSize: 10 }}>Select target and features to view analysis.</div>}
          </div>
        </Card>
      )}

      {sub === 'pca' && (
        <Card>
          <CardHead label="PCA" accent={C.violet} />
          <div style={{ padding: 14, display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(140px,1fr))', gap: 10 }}>
              <MetricTile label="NUMERIC FEATURES" value={numCols.length} color={C.cyan} />
              <Sel
                label="COMPONENTS"
                value={String(pcaK)}
                onChange={(v) => setPcaK(Math.max(2, Math.min(5, parseInt(v, 10) || 2)))}
                options={[2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n}` }))}
              />
            </div>
            <div style={{ fontSize: 10, color: C.t3 }}>
              PCA uses selected numeric columns; current set: {numCols.slice(0, 8).join(', ') || 'none'}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHead label="FEATURES PREVIEW (TARGET + SELECTED FEATURES)" accent={C.cyan} />
        <div style={{ padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.b1}`, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['head', 'tail', 'sample'].map((mode) => (
              <button key={mode} onClick={() => setPreviewMode(mode)} style={{ padding: '4px 9px', border: `1px solid ${previewMode === mode ? C.cyan : C.b1}`, background: previewMode === mode ? `${C.cyan}18` : 'transparent', color: previewMode === mode ? C.cyan : C.t3, borderRadius: 3, fontFamily: mono, fontSize: 9, cursor: 'pointer' }}>{mode.toUpperCase()}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[5, 10, 20].map((n) => (
              <button key={n} onClick={() => setPreviewN(n)} style={{ padding: '4px 9px', border: `1px solid ${previewN === n ? C.green : C.b1}`, background: previewN === n ? `${C.green}18` : 'transparent', color: previewN === n ? C.green : C.t3, borderRadius: 3, fontFamily: mono, fontSize: 9, cursor: 'pointer' }}>{n}</button>
            ))}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 760 }}>
            <thead>
              <tr style={{ background: C.deep }}>
                {previewHeaders.map((h) => <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: mono, letterSpacing: '.07em', borderBottom: `1px solid ${C.b1}` }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 ? `${C.raised}60` : 'transparent' }}>
                  {previewHeaders.map((h) => <td key={h} style={{ padding: '6px 10px', color: C.t2, fontFamily: mono }}>{String(row[h] ?? '')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {prepared && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(120px,1fr))', gap: 8 }}>
          <MetricTile label="ROWS USED" value={prepared.rowsUsed} color={C.green} />
          <MetricTile label="INPUT DIMS" value={prepared.inputSize} color={C.cyan} />
          <MetricTile label="OUTPUT DIMS" value={prepared.outputSize} color={C.violet} />
          <MetricTile label="TASK" value={prepared.task.toUpperCase()} color={C.gold} />
        </div>
      )}
    </div>
  );
}

function DLFeatureTabFromML({ ds, cfg, setCfg, dashboardCards }) {
  const mlConfig = useMemo(() => ({
    targetCol: cfg.target || '',
    featureCols: Array.isArray(cfg.features) ? cfg.features : [],
    encodings: {},
    scalingMethod: {},
  }), [cfg.target, cfg.features]);

  const experiment = useMemo(() => ({
    id: 'dl-features-proxy',
    name: 'DL Feature Engineering',
    dataSessionId: `${ds?.name || ''}:${ds?.rows?.length || 0}:${ds?.headers?.length || 0}`,
    ds,
    mlConfig,
    mlResults: {},
  }), [ds, mlConfig]);

  const handleUpdate = useCallback((patch) => {
    const nextCfg = patch?.mlConfig;
    if (!nextCfg) return;
    const nextTarget = nextCfg.targetCol || '';
    setCfg((prev) => ({
      ...prev,
      target: nextTarget || prev.target,
      features: Array.isArray(nextCfg.featureCols) ? nextCfg.featureCols : prev.features,
      task: nextTarget ? inferTask(ds, nextTarget) : prev.task,
    }));
  }, [setCfg, ds]);

  return (
    <MLLab
      ds={ds}
      subTab="features"
      experiment={experiment}
      onUpdate={handleUpdate}
      dashboardCards={dashboardCards}
    />
  );
}

function NetworkTab({ data, cfg, setCfg, trainCfg, setTrainCfg }) {
  const [forwardData, setForwardData] = useState(null);
  if (!data) return <div style={{ color: C.t4, fontSize: 11 }}>Prepare dataset in FEATURE ENGINEERING first.</div>;

  const hidden = parseLayerString(cfg.hiddenLayers);
  const hiddenActivations = useMemo(
    () => parseHiddenActivations(cfg.hiddenActivations, hidden.length, cfg.activation || 'relu'),
    [cfg.hiddenActivations, hidden.length, cfg.activation]
  );
  const layerSizes = [data.inputSize, ...hidden, data.outputSize];
  const parameterStats = useMemo(() => {
    let weights = 0;
    let biases = 0;
    for (let i = 0; i < layerSizes.length - 1; i += 1) {
      weights += layerSizes[i] * layerSizes[i + 1];
      biases += layerSizes[i + 1];
    }
    return {
      weights,
      biases,
      total: weights + biases,
    };
  }, [layerSizes]);

  const updateHiddenLayer = (index, value) => {
    const next = [...hidden];
    next[index] = clamp(parseInt(value || '1', 10) || 1, 1, 512);
    setCfg((prev) => ({ ...prev, hiddenLayers: next.join(',') }));
  };

  const updateHiddenActivation = (index, activation) => {
    const next = [...hiddenActivations];
    next[index] = activation;
    setCfg((prev) => ({ ...prev, hiddenActivations: next.join(',') }));
  };

  const addHiddenLayer = () => {
    const next = [...hidden, hidden[hidden.length - 1] || 8];
    const nextActs = [...hiddenActivations, cfg.activation || 'relu'];
    setCfg((prev) => ({ ...prev, hiddenLayers: next.join(','), hiddenActivations: nextActs.join(',') }));
  };

  const removeHiddenLayer = () => {
    if (hidden.length <= 1) return;
    const next = hidden.slice(0, -1);
    const nextActs = hiddenActivations.slice(0, -1);
    setCfg((prev) => ({ ...prev, hiddenLayers: next.join(','), hiddenActivations: nextActs.join(',') }));
  };

  const runForwardPass = () => {
    const input = data.XTrain?.[0];
    if (!input) return;
    const model = initParams(layerSizes, trainCfg.initializer);
    const cache = forwardPass(model, input, data.task, hiddenActivations);
    const tableRows = [];
    cache.zs.forEach((layerZ, layerIdx) => {
      layerZ.slice(0, 12).forEach((z, neuronIdx) => {
        tableRows.push({
          key: `${layerIdx}-${neuronIdx}`,
          layer: layerIdx === cache.zs.length - 1 ? 'OUTPUT' : `H${layerIdx + 1}`,
          neuron: neuronIdx + 1,
          z,
          a: cache.activations[layerIdx + 1][neuronIdx],
        });
      });
    });
    const layerRows = cache.activations.map((vals, idx) => {
      const arr = vals || [];
      const mean = arr.reduce((sum, value) => sum + value, 0) / Math.max(arr.length, 1);
      return {
        layer: idx === 0 ? 'INPUT' : idx === cache.activations.length - 1 ? 'OUTPUT' : `H${idx}`,
        mean,
        min: Math.min(...arr),
        max: Math.max(...arr),
      };
    });
    const flatActs = cache.activations.flat();
    const meanAbs = flatActs.reduce((sum, value) => sum + Math.abs(value), 0) / Math.max(flatActs.length, 1);
    const maxAbs = Math.max(...flatActs.map((value) => Math.abs(value)), 0);
    const deadRatio = (flatActs.filter((value) => Math.abs(value) < 1e-6).length / Math.max(flatActs.length, 1)) * 100;
    setForwardData({
      input,
      activations: cache.activations,
      tableRows,
      layerRows,
      line: cache.activations.map((vals, idx) => ({ x: idx, y: vals.reduce((sum, value) => sum + Math.abs(value), 0) / Math.max(vals.length, 1) })),
      meanAbs,
      maxAbs,
      deadRatio,
    });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 430px) minmax(0,1fr)', gap: 10 }}>
      <Card>
        <CardHead label="LAYER EDITOR + ACTIVATION + INIT" accent={C.cyan} />
        <div style={{ padding: 10, display: 'grid', gap: 10 }}>
          {layerSizes.map((size, idx) => {
            const isInput = idx === 0;
            const isOutput = idx === layerSizes.length - 1;
            const label = isInput ? 'INPUT' : isOutput ? 'OUTPUT' : `HIDDEN ${idx}`;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 68, textAlign: 'right', color: C.t3, fontSize: 8 }}>{label}</div>
                <input
                  type="number"
                  min={1}
                  max={512}
                  value={size}
                  disabled={isInput || isOutput}
                  onChange={(e) => !isInput && !isOutput && updateHiddenLayer(idx - 1, e.target.value)}
                  style={{ width: 52, background: C.b0, border: `1px solid ${C.b1}`, color: isInput || isOutput ? C.t3 : C.t1, padding: '4px 6px', borderRadius: 3, fontSize: 11 }}
                />
                {!isInput && !isOutput && (
                  <select
                    value={hiddenActivations[idx - 1] || 'relu'}
                    onChange={(e) => updateHiddenActivation(idx - 1, e.target.value)}
                    style={{ width: 88, background: C.b0, border: `1px solid ${C.b1}`, color: C.t1, padding: '4px 6px', borderRadius: 3, fontSize: 10, fontFamily: mono }}
                  >
                    {ACTIVATION_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option.toUpperCase()}</option>
                    ))}
                  </select>
                )}
                <div style={{ flex: 1, height: 4, background: C.b0, borderRadius: 2 }}>
                  <div style={{ width: `${Math.max(4, (size / 512) * 100)}%`, height: '100%', borderRadius: 2, background: isInput ? C.cyan : isOutput ? C.green : C.violet }} />
                </div>
                <div style={{ width: 18, color: C.t3, fontSize: 8 }}>{size}</div>
              </div>
            );
          })}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={addHiddenLayer} style={{ padding: '7px 8px', borderRadius: 3, border: `1px solid ${C.green}55`, background: `${C.green}16`, color: C.green, cursor: 'pointer', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', fontFamily: mono }}>+ LAYER</button>
            <button onClick={removeHiddenLayer} style={{ padding: '7px 8px', borderRadius: 3, border: `1px solid ${C.red}55`, background: `${C.red}16`, color: C.red, cursor: 'pointer', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', fontFamily: mono }}>- LAYER</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <MetricTile label="DEPTH" value={`${layerSizes.length} layers`} color={C.cyan} />
            <MetricTile label="TOTAL PARAMETERS" value={parameterStats.total.toLocaleString()} color={C.green} />
          </div>

          <div style={{ borderTop: `1px solid ${C.b1}`, paddingTop: 10, display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 10, color: C.gold, fontWeight: 700 }}>Activation + Init</div>
            <Sel
              label="ACTIVATION"
              value={cfg.activation}
              onChange={(activation) => setCfg((prev) => ({ ...prev, activation }))}
              options={ACTIVATION_OPTIONS.map((option) => ({ value: option, label: option.toUpperCase() }))}
            />
            <Sel
              label="INITIALIZER"
              value={trainCfg.initializer}
              onChange={(initializer) => setTrainCfg((prev) => ({ ...prev, initializer }))}
              options={[
                { value: 'xavier', label: 'XAVIER INIT' },
                { value: 'he', label: 'HE INIT' },
                { value: 'uniform', label: 'UNIFORM INIT' },
              ]}
            />
            <div style={{ fontSize: 10, color: C.t2 }}>Learning Rate: {Number(trainCfg.lr || 0).toFixed(3)}</div>
            <button onClick={runForwardPass} style={{ width: '100%', padding: '8px 10px', borderRadius: 3, border: `1px solid ${C.violet}66`, background: `${C.violet}18`, color: C.violet, fontFamily: mono, fontSize: 10, letterSpacing: '.1em', fontWeight: 700, cursor: 'pointer' }}>FORWARD PASS</button>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gap: 10 }}>
        <Card>
          <CardHead label="NETWORK TOPOLOGY (LIVE)" accent={C.cyan} />
          <div style={{ padding: 10 }}>
            <TopologyDiagram layerSizes={layerSizes} activation={new Set(hiddenActivations).size === 1 ? hiddenActivations[0] : 'mixed'} activations={forwardData?.activations} />
            <div style={{ display: 'flex', gap: 10, marginTop: 6, padding: '7px 8px', borderRadius: 3, background: C.surface, fontSize: 8, color: C.t3, flexWrap: 'wrap' }}>
              <span style={{ color: C.cyan }}>INPUT</span>
              <span style={{ color: C.violet }}>HIDDEN</span>
              <span style={{ color: C.green }}>OUTPUT</span>
              <span style={{ marginLeft: 'auto' }}>Nodes = neurons, edges = learned weights</span>
            </div>
          </div>
        </Card>

        {forwardData && (
          <>
            <Card>
              <CardHead label="FORWARD PASS" accent={C.green} />
              <div style={{ padding: 10, display: 'grid', gap: 8 }}>
                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0, padding: '8px 9px' }}>
                  <div style={{ fontSize: 8, color: C.t3, marginBottom: 4 }}>INPUT VECTOR</div>
                  <div style={{ fontSize: 10, color: C.t1, lineHeight: 1.5 }}>{forwardData.input.map((v) => v.toFixed(3)).join(', ')}</div>
                </div>
                <div style={{ maxHeight: 220, overflow: 'auto', border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                    <thead>
                      <tr style={{ background: C.surface }}>
                        <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Layer</th>
                        <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Neuron</th>
                        <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>z (pre)</th>
                        <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>a (post)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forwardData.tableRows.map((row) => (
                        <tr key={row.key}>
                          <td style={{ padding: '5px 7px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.layer}</td>
                          <td style={{ padding: '5px 7px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.neuron}</td>
                          <td style={{ padding: '5px 7px', color: C.gold, borderBottom: `1px solid ${C.b1}` }}>{row.z.toFixed(4)}</td>
                          <td style={{ padding: '5px 7px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{row.a.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
              <MetricTile label="MEAN |ACT|" value={forwardData.meanAbs.toFixed(3)} color={C.cyan} />
              <MetricTile label="MAX |ACT|" value={forwardData.maxAbs.toFixed(3)} color={C.green} />
              <MetricTile label="DEAD NODES" value={`${forwardData.deadRatio.toFixed(1)}%`} color={C.violet} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InitTab({ cfg, setCfg, prepared, networkCfg, trainState }) {
  const hidden = parseLayerString(networkCfg?.hiddenLayers);
  const layerSizes = prepared ? [prepared.inputSize, ...hidden, prepared.outputSize] : null;
  const optimizerFamily = resolveOptimizerFamily(cfg.optimizer);
  const needsMomentum = optimizerFamily === 'momentum';

  const previewModel = useMemo(() => {
    if (!layerSizes?.length) return null;
    return initParams(layerSizes, cfg.initializer);
  }, [layerSizes, cfg.initializer]);

  const weightValues = useMemo(() => {
    if (!previewModel?.weights?.length) return [];
    return previewModel.weights.flat(2);
  }, [previewModel]);

  const histData = useMemo(() => weightHistogram(weightValues, 26), [weightValues]);

  const layer0Rows = useMemo(() => {
    if (!previewModel?.weights?.[0]?.length) return [];
    const first = previewModel.weights[0];
    const rows = [];
    for (let out = 0; out < first.length; out += 1) {
      for (let inp = 0; inp < first[out].length; inp += 1) {
        rows.push({
          key: `${out}-${inp}`,
          from: `x${inp + 1}`,
          to: `h1_${out + 1}`,
          value: first[out][inp],
        });
      }
    }
    return rows.slice(0, 180);
  }, [previewModel]);

  const activeLr = cfg.useCustomLr ? cfg.lr : 0.01;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <Card>
        <CardHead label="INITIALIZATION STUDIO" accent={C.gold} />
        <div style={{ padding: 10, display: 'grid', gridTemplateColumns: 'minmax(300px, 430px) minmax(0,1fr)', gap: 10 }}>
          <div style={{ display: 'grid', gap: 10 }}>
            <Sel
              label="WEIGHT INITIALIZER"
              value={cfg.initializer}
              onChange={(initializer) => setCfg((p) => ({ ...p, initializer }))}
              options={[
                { value: 'xavier', label: 'XAVIER' },
                { value: 'he', label: 'HE' },
                { value: 'uniform', label: 'UNIFORM' },
                { value: 'normal', label: 'NORMAL' },
                { value: 'zeros', label: 'ZEROS' },
              ]}
            />

            <Sel
              label="OPTIMIZER (8 OPTIONS)"
              value={cfg.optimizer}
              onChange={(optimizer) => setCfg((p) => ({ ...p, optimizer }))}
              options={OPTIMIZER_OPTIONS}
            />

            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: '9px 10px', display: 'grid', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.t2, fontSize: 10, fontFamily: mono }}>
                <input
                  type="checkbox"
                  checked={!!cfg.useCustomLr}
                  onChange={(event) => setCfg((p) => ({ ...p, useCustomLr: event.target.checked }))}
                />
                Use custom learning rate (default = 0.01)
              </label>
              <input
                type="number"
                step="0.0001"
                min="0.00001"
                value={String(cfg.lr)}
                disabled={!cfg.useCustomLr}
                onChange={(event) => setCfg((p) => ({ ...p, lr: Math.max(1e-5, parseFloat(event.target.value) || 0.01) }))}
                style={{ width: '100%', background: C.b0, border: `1px solid ${C.b1}`, color: cfg.useCustomLr ? C.t1 : C.t4, padding: '7px 8px', borderRadius: 3, fontFamily: mono, fontSize: 10 }}
              />
              <div style={{ fontSize: 9, color: C.t3 }}>Active LR: {activeLr.toFixed(5)}</div>
            </div>

            {needsMomentum && (
              <div style={{ display: 'grid', gap: 6 }}>
                <TxtInput
                  label="MOMENTUM (ONLY FOR MOMENTUM/NESTEROV)"
                  value={String(cfg.momentum)}
                  onChange={(value) => setCfg((p) => ({ ...p, momentum: clamp(parseFloat(value) || 0.9, 0, 0.999) }))}
                />
                <div style={{ fontSize: 9, color: C.t3 }}>Used only when optimizer is MOMENTUM or NESTEROV.</div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <MetricTile label="LAYERS" value={layerSizes?.length || '-'} color={C.cyan} />
              <MetricTile label="WEIGHTS PREVIEW" value={weightValues.length.toLocaleString()} color={C.violet} />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: '8px 8px 2px' }}>
              <div style={{ fontSize: 9, color: C.gold, fontFamily: mono, marginBottom: 6, letterSpacing: '.08em' }}>WEIGHTS HISTOGRAM DISTRIBUTION</div>
              <div style={{ height: 210 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={histData} margin={{ top: 6, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
                    <XAxis dataKey="range" hide />
                    <YAxis stroke={C.t4} fontSize={9} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="count" fill={C.cyan} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, overflow: 'hidden' }}>
              <div style={{ fontSize: 9, color: C.green, fontFamily: mono, letterSpacing: '.08em', padding: '8px 10px', borderBottom: `1px solid ${C.b1}` }}>LAYER-1 WEIGHTS TABLE (SNAPSHOT)</div>
              <div style={{ maxHeight: 220, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                  <thead>
                    <tr style={{ background: C.deep }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>From</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>To</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {layer0Rows.map((row) => (
                      <tr key={row.key}>
                        <td style={{ padding: '5px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.from}</td>
                        <td style={{ padding: '5px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.to}</td>
                        <td style={{ padding: '5px 8px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{row.value.toFixed(5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {trainState?.model?.weights?.length ? (
              <div style={{ fontSize: 9, color: C.t3, fontFamily: mono }}>
                Current trained model snapshot available: {trainState.model.weights.flat(2).length.toLocaleString()} weights
              </div>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  );
}

function TrainTab({ prepared, networkCfg, trainCfg, setTrainCfg, trainState, onStart, onStop, running }) {
  const [view, setView] = useState('training');
  const [hiddenSeries, setHiddenSeries] = useState({});

  const toggleSeries = useCallback((key) => {
    if (!key) return;
    setHiddenSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (!prepared) return <div style={{ color: C.t4, fontSize: 11 }}>Prepare dataset and network before training.</div>;

  const history = trainState.history || [];
  const hidden = parseLayerString(networkCfg.hiddenLayers);
  const layerSizes = [prepared.inputSize, ...hidden, prepared.outputSize];
  const isClassification = prepared.task === 'classification';
  const latestTrainMetrics = trainState.trainMetrics;
  const latestValMetrics = trainState.valMetrics;
  const classLabels = prepared.classList?.length
    ? prepared.classList
    : Array.from({ length: prepared.outputSize || 0 }, (_, i) => `Class ${i}`);
  const hasTrainingResult = history.length > 0;
  const primaryActionLabel = running ? 'STOP TRAINING' : hasTrainingResult ? 'RESTART TRAINING' : 'START TRAINING';
  const primaryActionHandler = running ? onStop : onStart;
  const canShowMetrics = hasTrainingResult && !running;
  const metricsActionLabel = view === 'metrics' ? 'SHOW TRAINING' : 'SHOW METRICS';
  const valReportVisual = (latestValMetrics?.report || []).map((row) => ({
    className: classLabels[row.classIndex] ?? `Class ${row.classIndex}`,
    precision: row.precision,
    recall: row.recall,
    f1: row.f1,
  }));
  const confusionMax = Math.max(...((latestValMetrics?.confusion || []).flat()), 1);

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <Card>
        {/* <CardHead label="TRAINING CONTROL" accent={C.orange} /> */}
        <div style={{ padding: 10, display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,1fr) minmax(120px,1fr) minmax(120px,1fr) minmax(140px,1fr) minmax(140px,1fr)', gap: 8, alignItems: 'end' }}>
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ fontSize: 9, color: C.t4, letterSpacing: '.08em', fontFamily: mono }}>EPOCHS</div>
              <input
                type="number"
                min={1}
                max={500}
                value={String(trainCfg.epochs)}
                onChange={(event) => setTrainCfg((p) => ({ ...p, epochs: Math.max(1, Math.min(500, parseInt(event.target.value || '1', 10))) }))}
                style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 8px', fontSize: 10, fontFamily: mono, borderRadius: 3, outline: 'none', height: 30 }}
              />
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ fontSize: 9, color: C.t4, letterSpacing: '.08em', fontFamily: mono }}>BATCH SIZE</div>
              <input
                type="number"
                min={1}
                max={2048}
                value={String(trainCfg.batchSize)}
                onChange={(event) => setTrainCfg((p) => ({ ...p, batchSize: Math.max(1, Math.min(2048, parseInt(event.target.value || '1', 10))) }))}
                style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 8px', fontSize: 10, fontFamily: mono, borderRadius: 3, outline: 'none', height: 30 }}
              />
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ fontSize: 9, color: C.t4, letterSpacing: '.08em', fontFamily: mono }}>VALIDATION SPLIT</div>
              <select
                value={String(trainCfg.valSplit || 0.2)}
                onChange={(event) => setTrainCfg((p) => ({ ...p, valSplit: clamp(parseFloat(event.target.value) || 0.2, 0.1, 0.3) }))}
                style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 8px', fontSize: 10, fontFamily: mono, borderRadius: 3, outline: 'none', height: 30 }}
              >
                <option value="0.1">10%</option>
                <option value="0.2">20%</option>
                <option value="0.3">30%</option>
              </select>
            </div>

            <button
              onClick={primaryActionHandler}
              style={{ height: 30, borderRadius: 3, border: `1px solid ${running ? C.violet : C.orange}`, background: running ? `${C.violet}18` : `${C.orange}18`, color: running ? C.violet : C.orange, fontFamily: mono, fontSize: 10, letterSpacing: '.08em', fontWeight: 700, cursor: 'pointer' }}
            >
              {primaryActionLabel}
            </button>

            <button
              onClick={() => canShowMetrics && setView((prev) => (prev === 'metrics' ? 'training' : 'metrics'))}
              disabled={!canShowMetrics}
              style={{ height: 30, borderRadius: 3, border: `1px solid ${canShowMetrics ? C.green : C.b1}`, background: canShowMetrics ? `${C.green}18` : 'transparent', color: canShowMetrics ? C.green : C.t4, fontFamily: mono, fontSize: 10, letterSpacing: '.08em', fontWeight: 700, cursor: canShowMetrics ? 'pointer' : 'not-allowed', opacity: canShowMetrics ? 1 : 0.6 }}
            >
              {metricsActionLabel}
            </button>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(120px,1fr))', gap: 8 }}>
        <MetricTile label="EPOCH" value={trainState.epoch || 0} color={C.orange} />
        <MetricTile label="TRAIN LOSS" value={fnum(trainState.loss)} color={C.red} />
        <MetricTile label="VAL LOSS" value={fnum(trainState.valLoss)} color={C.orange} />
        <MetricTile label="TRAIN ACC" value={trainState.acc == null ? '-' : `${(trainState.acc * 100).toFixed(2)}%`} color={C.green} />
        <MetricTile label="VAL ACC" value={trainState.valAcc == null ? '-' : `${(trainState.valAcc * 100).toFixed(2)}%`} color={C.cyan} />
      </div>

      {view === 'training' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 10 }}>
            <Card>
              <CardHead label="TRAINING GRAPHS" accent={C.pink} />
              <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 8, padding: 8, height: 300 }}>
                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: '6px 8px 2px' }}>
                  <div style={{ fontSize: 9, color: C.orange, letterSpacing: '.08em', fontFamily: mono, marginBottom: 3 }}>TRAIN: LOSS + ACCURACY</div>
                  <div style={{ height: 142 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={history}>
                        <CartesianGrid stroke={C.b0} strokeDasharray="3 3" />
                        <XAxis dataKey="epoch" stroke={C.t4} fontSize={8} label={{ value: 'Epoch', position: 'insideBottom', offset: -2, fill: C.t4, fontSize: 8 }} />
                        <YAxis stroke={C.t4} fontSize={8} label={{ value: 'Value', angle: -90, position: 'insideLeft', fill: C.t4, fontSize: 8 }} />
                        <Tooltip content={<ChartTip />} />
                        <Legend onClick={(entry) => toggleSeries(entry?.dataKey)} wrapperStyle={{ fontSize: 8, cursor: 'pointer' }} />
                        {!hiddenSeries.trainLoss && <Line type="monotone" dataKey="trainLoss" name="Train Loss" stroke={C.orange} strokeWidth={2} dot={false} />}
                        {isClassification && !hiddenSeries.trainAcc && <Line type="monotone" dataKey="trainAcc" name="Train Acc" stroke={C.green} strokeWidth={2} dot={false} />}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: '6px 8px 2px' }}>
                  <div style={{ fontSize: 9, color: C.cyan, letterSpacing: '.08em', fontFamily: mono, marginBottom: 3 }}>VALIDATION: LOSS + ACCURACY</div>
                  <div style={{ height: 142 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={history}>
                        <CartesianGrid stroke={C.b0} strokeDasharray="3 3" />
                        <XAxis dataKey="epoch" stroke={C.t4} fontSize={8} label={{ value: 'Epoch', position: 'insideBottom', offset: -2, fill: C.t4, fontSize: 8 }} />
                        <YAxis stroke={C.t4} fontSize={8} label={{ value: 'Value', angle: -90, position: 'insideLeft', fill: C.t4, fontSize: 8 }} />
                        <Tooltip content={<ChartTip />} />
                        <Legend onClick={(entry) => toggleSeries(entry?.dataKey)} wrapperStyle={{ fontSize: 8, cursor: 'pointer' }} />
                        {!hiddenSeries.valLoss && <Line type="monotone" dataKey="valLoss" name="Val Loss" stroke={C.gold} strokeWidth={2} dot={false} />}
                        {isClassification && !hiddenSeries.valAcc && <Line type="monotone" dataKey="valAcc" name="Val Acc" stroke={C.cyan} strokeWidth={2} dot={false} />}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <CardHead label="LIVE NETWORK" accent={C.violet} />
              <div style={{ padding: 8 }}>
                <TopologyDiagram layerSizes={layerSizes} activation={networkCfg.activation} activations={trainState.activations} />
              </div>
            </Card>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Card>
              <CardHead label="WEIGHT FIELD (LAYER 1)" accent={C.cyan} />
              <div style={{ padding: 10 }}><WeightHeatmap model={trainState.model} /></div>
            </Card>
            <Card>
              <CardHead label="GRADIENT FLOW" accent={C.violet} />
              <div style={{ height: 180, padding: 8 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={history.slice(-18)}>
                    <CartesianGrid stroke={C.b0} strokeDasharray="3 3" />
                    <XAxis dataKey="epoch" stroke={C.t4} fontSize={9} />
                    <YAxis stroke={C.t4} fontSize={9} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="gradNorm" fill={C.violet} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </>
      )}

      {view === 'metrics' && (
        <div style={{ display: 'grid', gap: 10 }}>
          {isClassification ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(140px,1fr))', gap: 8 }}>
                <MetricTile label="TRAIN PRECISION" value={latestTrainMetrics?.precision == null ? '-' : latestTrainMetrics.precision.toFixed(4)} color={C.green} />
                <MetricTile label="TRAIN RECALL" value={latestTrainMetrics?.recall == null ? '-' : latestTrainMetrics.recall.toFixed(4)} color={C.cyan} />
                <MetricTile label="TRAIN F1" value={latestTrainMetrics?.f1 == null ? '-' : latestTrainMetrics.f1.toFixed(4)} color={C.violet} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(140px,1fr))', gap: 8 }}>
                <MetricTile label="VAL PRECISION" value={latestValMetrics?.precision == null ? '-' : latestValMetrics.precision.toFixed(4)} color={C.green} />
                <MetricTile label="VAL RECALL" value={latestValMetrics?.recall == null ? '-' : latestValMetrics.recall.toFixed(4)} color={C.cyan} />
                <MetricTile label="VAL F1" value={latestValMetrics?.f1 == null ? '-' : latestValMetrics.f1.toFixed(4)} color={C.violet} />
              </div>

              <Card>
                <CardHead label="CLASSIFICATION REPORT (VALIDATION)" accent={C.green} />
                <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 520 }}>
                      <thead>
                        <tr style={{ background: C.deep }}>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Class</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Precision</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Recall</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>F1</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Support</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(latestValMetrics?.report || []).map((row) => (
                          <tr key={row.classIndex}>
                            <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{classLabels[row.classIndex] ?? `Class ${row.classIndex}`}</td>
                            <td style={{ padding: '6px 8px', color: C.green, borderBottom: `1px solid ${C.b1}` }}>{row.precision.toFixed(4)}</td>
                            <td style={{ padding: '6px 8px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{row.recall.toFixed(4)}</td>
                            <td style={{ padding: '6px 8px', color: C.violet, borderBottom: `1px solid ${C.b1}` }}>{row.f1.toFixed(4)}</td>
                            <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.support}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ height: 260, border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: 8 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={valReportVisual} margin={{ top: 10, right: 8, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
                        <XAxis dataKey="className" stroke={C.t4} fontSize={9} interval={0} angle={-20} textAnchor="end" height={42} />
                        <YAxis stroke={C.t4} fontSize={9} domain={[0, 1]} />
                        <Tooltip content={<ChartTip />} />
                        <Legend wrapperStyle={{ fontSize: 9 }} />
                        <Bar dataKey="precision" fill={C.green} radius={[2, 2, 0, 0]} />
                        <Bar dataKey="recall" fill={C.cyan} radius={[2, 2, 0, 0]} />
                        <Bar dataKey="f1" fill={C.violet} radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>

              <Card>
                <CardHead label="CONFUSION MATRIX (VALIDATION)" accent={C.cyan} />
                <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 520 }}>
                      <thead>
                        <tr style={{ background: C.deep }}>
                          <th style={{ border: `1px solid ${C.b1}`, padding: 6, color: C.t4, fontFamily: mono }}>True \ Pred</th>
                          {classLabels.map((label) => (
                            <th key={label} style={{ border: `1px solid ${C.b1}`, padding: 6, color: C.t2, fontFamily: mono }}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(latestValMetrics?.confusion || []).map((row, i) => (
                          <tr key={i}>
                            <td style={{ border: `1px solid ${C.b1}`, padding: 6, color: C.t2, fontFamily: mono }}>{classLabels[i] ?? `Class ${i}`}</td>
                            {row.map((val, j) => (
                              <td key={j} style={{ border: `1px solid ${C.b1}`, padding: 6, textAlign: 'center', color: i === j ? C.green : C.t2 }}>{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(classLabels.length, 1)}, minmax(32px,1fr))`, gap: 4 }}>
                      {(latestValMetrics?.confusion || []).flatMap((row, i) => row.map((val, j) => {
                        const p = val / Math.max(confusionMax, 1);
                        const fill = `rgba(34,211,238,${0.1 + p * 0.9})`;
                        return (
                          <div
                            key={`${i}-${j}`}
                            title={`True ${classLabels[i] ?? i} → Pred ${classLabels[j] ?? j}: ${val}`}
                            style={{
                              minHeight: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 3,
                              border: `1px solid ${C.b1}`,
                              background: fill,
                              color: p > 0.55 ? C.void : C.t1,
                              fontSize: 10,
                              fontFamily: mono,
                              fontWeight: 700,
                            }}
                          >
                            {val}
                          </div>
                        );
                      }))}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 9, color: C.t3, fontFamily: mono }}>Darker cell = higher count</div>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <div style={{ color: C.t4, fontSize: 11 }}>Precision/Recall/F1 and classification report are available only for classification tasks.</div>
          )}
        </div>
      )}
    </div>
  );
}

function TestTab({ prepared, trainState, networkCfg }) {
  const [source, setSource] = useState('train');
  const [mode, setMode] = useState('single');
  const [rowIndex, setRowIndex] = useState(0);
  const [uploaded, setUploaded] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [predRows, setPredRows] = useState([]);

  if (!prepared) return <div style={{ color: C.t4, fontSize: 11 }}>Upload data first.</div>;
  if (!trainState?.model?.weights?.length) return <div style={{ color: C.t4, fontSize: 11 }}>Train model first to enable prediction testing.</div>;

  const hidden = parseLayerString(networkCfg.hiddenLayers);
  const hiddenActs = parseHiddenActivations(networkCfg.hiddenActivations, hidden.length, networkCfg.activation || 'relu');
  const layerSizes = [prepared.inputSize, ...hidden, prepared.outputSize];
  const model = trainState.model;
  const trainRows = prepared.XTrain || [];
  const valRows = prepared.XTest || [];
  const uploadedVectors = uploaded?.rows ? encodeRowsForPrediction(uploaded.rows, prepared) : [];
  const sourceRows = source === 'train' ? trainRows : source === 'validation' ? valRows : uploadedVectors;
  const maxIndex = Math.max(0, sourceRows.length - 1);
  const safeIndex = clamp(rowIndex || 0, 0, maxIndex);

  const onUploadFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseCSV(text);
    if (!parsed?.rows?.length) {
      setUploaded(null);
      setUploadStatus('Failed to parse file for prediction.');
      return;
    }
    setUploaded(parsed);
    setUploadStatus(`Loaded ${file.name} · ${parsed.rows.length} rows`);
  };

  const runPrediction = () => {
    if (!sourceRows.length) {
      setPredRows([]);
      return;
    }
    const vectors = mode === 'single' ? [sourceRows[safeIndex]] : sourceRows;
    const out = runPredictions(model, prepared, hiddenActs, vectors);
    setPredRows(out);
  };

  const downloadPredictions = () => {
    if (source !== 'upload' || !uploaded?.rows?.length || !predRows.length) return;
    const baseHeaders = uploaded.headers || [];
    const extraHeaders = prepared.task === 'classification' ? ['prediction', 'confidence'] : ['prediction'];
    const headerLine = [...baseHeaders, ...extraHeaders].join(',');
    const dataLines = predRows.map((pred, idx) => {
      const row = uploaded.rows[idx] || {};
      const base = baseHeaders.map((h) => JSON.stringify(String(row[h] ?? ''))).join(',');
      const tail = prepared.task === 'classification'
        ? [JSON.stringify(String(pred.prediction)), JSON.stringify(String((pred.confidence ?? 0).toFixed(6)))].join(',')
        : [JSON.stringify(String((pred.prediction ?? 0).toFixed(6)))].join(',');
      return `${base},${tail}`;
    });
    const blob = new Blob([[headerLine, ...dataLines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'predictions.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const currentPred = predRows[0];
  const totalWeights = model.weights.flat(2).length;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card>
        <CardHead label="FINAL MODEL SNAPSHOT" accent={C.violet} />
        <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 10 }}>
          <div>
            <TopologyDiagram layerSizes={layerSizes} activation={networkCfg.activation} activations={trainState.activations} />
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <MetricTile label="ARCHITECTURE" value={layerSizes.join(' → ')} color={C.cyan} />
            <MetricTile label="TOTAL WEIGHTS" value={totalWeights.toLocaleString()} color={C.green} />
            <Card>
              <CardHead label="FINAL WEIGHTS (LAYER 1)" accent={C.cyan} />
              <div style={{ padding: 8 }}><WeightHeatmap model={model} /></div>
            </Card>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead label="PREDICTION TEST LAB" accent={C.orange} />
        <div style={{ padding: 10, display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(140px,1fr))', gap: 8, alignItems: 'end' }}>
            <Sel
              label="SOURCE"
              value={source}
              onChange={(value) => { setSource(value); setPredRows([]); }}
              options={[
                { value: 'train', label: 'TRAINING DATA' },
                { value: 'validation', label: 'VALIDATION DATA' },
                { value: 'upload', label: 'UPLOAD CSV' },
              ]}
            />
            <Sel
              label="MODE"
              value={mode}
              onChange={(value) => setMode(value)}
              options={[
                { value: 'single', label: 'SINGLE' },
                { value: 'entire', label: 'ENTIRE DATASET' },
              ]}
            />
            <TxtInput
              label="ROW INDEX"
              value={String(safeIndex)}
              onChange={(value) => setRowIndex(Math.max(0, parseInt(value || '0', 10) || 0))}
              style={{ opacity: mode === 'single' ? 1 : 0.5 }}
            />
            <Btn onClick={runPrediction} color={C.orange}>RUN PREDICTION</Btn>
          </div>

          {source === 'upload' && (
            <div style={{ display: 'grid', gap: 8 }}>
              <input type="file" accept=".csv,text/csv" onChange={(e) => onUploadFile(e.target.files?.[0])} style={{ color: C.t2, fontSize: 11 }} />
              <div style={{ fontSize: 10, color: C.t3 }}>{uploadStatus || 'Upload CSV with matching feature columns.'}</div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 10, color: C.t3 }}>
              Source rows: <b>{sourceRows.length}</b>
            </div>
            {source === 'upload' && predRows.length > 0 && (
              <Btn onClick={downloadPredictions} color={C.violet}>DOWNLOAD PREDICTIONS CSV</Btn>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <CardHead label="PREDICTION OUTPUT" accent={C.green} />
        <div style={{ padding: 10, display: 'grid', gap: 10 }}>
          {!predRows.length && <div style={{ color: C.t4, fontSize: 11 }}>Run prediction for single row or entire dataset.</div>}

          {predRows.length > 0 && mode === 'single' && (
            <div style={{ border: `1px solid ${C.green}66`, background: `${C.green}12`, borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontSize: 9, color: C.t4, letterSpacing: '.08em', fontFamily: mono }}>PREDICTION (HIGHLIGHTED)</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.green, marginTop: 4 }}>
                {prepared.task === 'classification' ? String(currentPred?.prediction || '-') : fnum(currentPred?.prediction, 6)}
              </div>
              {prepared.task === 'classification' && (
                <div style={{ fontSize: 10, color: C.t3, marginTop: 3 }}>Confidence: {((currentPred?.confidence || 0) * 100).toFixed(2)}%</div>
              )}
            </div>
          )}

          {predRows.length > 0 && prepared.task === 'classification' && mode === 'single' && (
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(currentPred?.scores || []).map((score, idx) => ({ cls: prepared.classList[idx] || `Class ${idx}`, score }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
                  <XAxis dataKey="cls" stroke={C.t4} fontSize={9} />
                  <YAxis stroke={C.t4} fontSize={9} domain={[0, 1]} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="score" fill={C.cyan} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {predRows.length > 0 && (
            <div style={{ maxHeight: 260, overflow: 'auto', border: `1px solid ${C.b1}`, borderRadius: 3 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: C.deep }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>#</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Prediction</th>
                    {prepared.task === 'classification' && <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Confidence</th>}
                  </tr>
                </thead>
                <tbody>
                  {predRows.slice(0, 500).map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{idx}</td>
                      <td style={{ padding: '6px 8px', color: C.green, borderBottom: `1px solid ${C.b1}`, fontWeight: idx === 0 && mode === 'single' ? 700 : 500 }}>
                        {prepared.task === 'classification' ? String(row.prediction) : fnum(row.prediction, 6)}
                      </td>
                      {prepared.task === 'classification' && (
                        <td style={{ padding: '6px 8px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{((row.confidence || 0) * 100).toFixed(2)}%</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function MetricsTab({ history, prepared, evalResult }) {
  const latest = history[history.length - 1];
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(110px,1fr))', gap: 8 }}>
        <MetricTile label="EPOCHS" value={history.length} color={C.cyan} />
        <MetricTile label="FINAL LOSS" value={fnum(latest?.trainLoss)} color={C.orange} />
        <MetricTile label="FINAL VAL LOSS" value={fnum(latest?.valLoss)} color={C.violet} />
        <MetricTile label="TASK" value={(prepared?.task || '-').toUpperCase()} color={C.green} />
        <MetricTile label="TEST LOSS" value={fnum(evalResult?.testLoss)} color={C.gold} />
      </div>

      <Card>
        <CardHead label="LOSS AREA" accent={C.orange} />
        <div style={{ height: 230, padding: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history}>
              <CartesianGrid stroke={C.b0} strokeDasharray="3 3" />
              <XAxis dataKey="epoch" stroke={C.t4} fontSize={9} />
              <YAxis stroke={C.t4} fontSize={9} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="trainLoss" stroke={C.orange} fill={`${C.orange}44`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

function ReportTab({ ds, prepared, networkCfg, trainCfg, trainState, evalResult, onMarkDone, dashboardCards, profile, reportState = null, onReportStateChange = null }) {
  const history = trainState?.history || [];
  const hidden = parseLayerString(networkCfg?.hiddenLayers);
  const layerSizes = [prepared?.inputSize || '?', ...hidden, prepared?.outputSize || '?'];
  const hiddenActs = parseHiddenActivations(networkCfg?.hiddenActivations, hidden.length, networkCfg?.activation || 'relu');
  const classLabels = prepared?.classList?.length
    ? prepared.classList
    : Array.from({ length: prepared?.outputSize || 0 }, (_, i) => `Class ${i}`);
  const valReport = trainState?.valMetrics?.report || [];
  const valConfusion = trainState?.valMetrics?.confusion || [];
  const confusionMax = Math.max(...valConfusion.flat(), 1);
  const fullArch = layerSizes.join(' → ');
  const outputActLabel = prepared?.task === 'classification'
    ? String(prepared?.outputSize > 1 ? 'softmax' : 'sigmoid').toUpperCase()
    : null;
  const activationFlow = ['INPUT', ...(hiddenActs || []).map((act) => String(act).toUpperCase()), ...(outputActLabel ? [outputActLabel] : [])];
  const fullActivationFlow = activationFlow.join(' → ');

  const reportExperiment = useMemo(() => ({
    id: `dl-report-${ds?.rows?.length || 0}-${prepared?.rowsUsed || 0}`,
    name: 'DL Experiment',
    moduleType: 'machine_learning',
    readOnly: false,
    reportState,
    ds,
    mlConfig: {
      targetCol: prepared?.target || '',
      featureCols: prepared?.features || [],
      encodings: {},
      scalingMethod: {},
    },
    mlResults: {
      ann_model: {
        modelName: 'Neural Network (ANN)',
        taskType: prepared?.task || 'classification',
        trainN: prepared?.XTrain?.length || 0,
        testN: prepared?.XTest?.length || 0,
        metrics: prepared?.task === 'classification'
          ? {
            accuracy: trainState?.valAcc ?? trainState?.acc ?? 0,
            precision: trainState?.valMetrics?.precision ?? 0,
            recall: trainState?.valMetrics?.recall ?? 0,
            f1: trainState?.valMetrics?.f1 ?? 0,
          }
          : {
            mae: evalResult?.mae ?? 0,
            mse: (evalResult?.rmse ?? 0) ** 2,
            rmse: evalResult?.rmse ?? 0,
            r2: evalResult?.r2 ?? 0,
          },
      },
    },
  }), [ds, prepared, trainState, evalResult, reportState]);

  const handleReportExperimentUpdate = useCallback((patch) => {
    if (patch?.reportState && typeof onReportStateChange === 'function') {
      onReportStateChange(patch.reportState);
    }
  }, [onReportStateChange]);

  const exportReport = () => {
    const payload = {
      generated_at: new Date().toISOString(),
      dataset: ds ? { rows: ds.rows.length, cols: ds.headers.length, headers: ds.headers } : null,
      prep: prepared ? {
        task: prepared.task,
        features: prepared.features,
        target: prepared.target,
        split: prepared.split,
        rows_used: prepared.rowsUsed,
      } : null,
      network: networkCfg,
      training: trainCfg,
      final: {
        epoch: trainState.epoch,
        loss: trainState.loss,
        val_loss: trainState.valLoss,
        acc: trainState.acc,
        val_acc: trainState.valAcc,
        grad_norm: trainState.gradNorm,
        weight_rms: trainState.weightRms,
      },
      train_metrics: trainState.trainMetrics,
      validation_metrics: trainState.valMetrics,
      history,
      eval: evalResult,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'dl-experiment-report.json';
    a.click();
    URL.revokeObjectURL(a.href);
    onMarkDone?.();
  };

  if (!ds) return <div style={{ color: C.t4, fontSize: 11 }}>Upload data first.</div>;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <MLLab
        key={`dl-report-like-ml-${ds?.rows?.length || 0}-${prepared?.rowsUsed || 0}`}
        ds={ds}
        subTab="report"
        experiment={reportExperiment}
        onUpdate={handleReportExperimentUpdate}
        dashboardCards={dashboardCards || []}
      />

      <Card>
        <CardHead label="DL EXPERIMENT DETAILS" accent={C.violet} />
        <div style={{ padding: 12, display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: 8 }}>
              <div style={{ fontSize: 10, color: C.violet, letterSpacing: '.08em', fontFamily: mono, marginBottom: 6 }}>NEURAL ARCH</div>
              <div
                title={fullArch}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 6,
                  maxHeight: 52,
                  overflowY: 'auto',
                  paddingRight: 2,
                }}
              >
                {layerSizes.map((size, index) => (
                  <React.Fragment key={`layer-box-${index}`}>
                    <div style={{ padding: '4px 8px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep, color: C.t1, fontSize: 10, fontFamily: mono }}>
                      {size}
                    </div>
                    {index < layerSizes.length - 1 && <span style={{ color: C.t4, fontSize: 10, fontFamily: mono }}>→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: 8 }}>
              <div style={{ fontSize: 10, color: C.cyan, letterSpacing: '.08em', fontFamily: mono, marginBottom: 6 }}>ACTIVATION FLOW</div>
              <div
                title={fullActivationFlow}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 6,
                  maxHeight: 52,
                  overflowY: 'auto',
                  paddingRight: 2,
                }}
              >
                {activationFlow.map((act, index) => (
                  <React.Fragment key={`act-flow-box-${index}`}>
                    <div style={{ padding: '4px 8px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep, color: C.t1, fontSize: 10, fontFamily: mono }}>
                      {act}
                    </div>
                    {index < activationFlow.length - 1 && <span style={{ color: C.t4, fontSize: 10, fontFamily: mono }}>→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(140px,1fr))', gap: 8 }}>
            <MetricTile
              label="OUTPUT ACT"
              value={String(prepared?.task === 'classification' ? (prepared?.outputSize > 1 ? 'softmax' : 'sigmoid') : 'linear').toUpperCase()}
              color={C.green}
            />
            <MetricTile label="OPTIMIZER" value={String(trainCfg?.optimizer || '-').toUpperCase()} color={C.orange} />
            <MetricTile label="INITIALIZER" value={String(trainCfg?.initializer || '-').toUpperCase()} color={C.gold} />
            <MetricTile label="LEARNING RATE" value={(trainCfg?.useCustomLr ? trainCfg?.lr : 0.01).toFixed(5)} color={C.red} />
            <MetricTile label="EPOCHS / BATCH" value={`${trainCfg?.epochs || '-'} / ${trainCfg?.batchSize || '-'}`} color={C.cyan} />
            <MetricTile label="VAL SPLIT" value={`${Math.round((trainCfg?.valSplit ?? 0.2) * 100)}%`} color={C.violet} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 10 }}>
            <Card>
              <CardHead label="NETWORK DIAGRAM" accent={C.cyan} />
              <div style={{ padding: 8 }}>
                <TopologyDiagram layerSizes={layerSizes} activation={networkCfg.activation} activations={trainState.activations} />
              </div>
            </Card>
            <Card>
              <CardHead label="WEIGHT MAP (LAYER 1)" accent={C.violet} />
              <div style={{ padding: 8 }}><WeightHeatmap model={trainState.model} /></div>
            </Card>
          </div>

          <Card>
            <CardHead label="TRAINING CURVES" accent={C.orange} />
            <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 8, padding: 8, height: 320 }}>
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: '6px 8px 2px' }}>
                <div style={{ fontSize: 9, color: C.orange, letterSpacing: '.08em', fontFamily: mono, marginBottom: 3 }}>TRAIN LOSS + TRAIN ACC</div>
                <div style={{ height: 128 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={history}>
                      <CartesianGrid stroke={C.b0} strokeDasharray="3 3" />
                      <XAxis dataKey="epoch" stroke={C.t4} fontSize={8} />
                      <YAxis stroke={C.t4} fontSize={8} />
                      <Tooltip content={<ChartTip />} />
                      <Legend wrapperStyle={{ fontSize: 8 }} />
                      <Line type="monotone" dataKey="trainLoss" name="Train Loss" stroke={C.orange} strokeWidth={2} dot={false} />
                      {prepared?.task === 'classification' && <Line type="monotone" dataKey="trainAcc" name="Train Acc" stroke={C.green} strokeWidth={2} dot={false} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: '6px 8px 2px' }}>
                <div style={{ fontSize: 9, color: C.cyan, letterSpacing: '.08em', fontFamily: mono, marginBottom: 3 }}>VAL LOSS + VAL ACC</div>
                <div style={{ height: 128 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={history}>
                      <CartesianGrid stroke={C.b0} strokeDasharray="3 3" />
                      <XAxis dataKey="epoch" stroke={C.t4} fontSize={8} />
                      <YAxis stroke={C.t4} fontSize={8} />
                      <Tooltip content={<ChartTip />} />
                      <Legend wrapperStyle={{ fontSize: 8 }} />
                      <Line type="monotone" dataKey="valLoss" name="Val Loss" stroke={C.gold} strokeWidth={2} dot={false} />
                      {prepared?.task === 'classification' && <Line type="monotone" dataKey="valAcc" name="Val Acc" stroke={C.cyan} strokeWidth={2} dot={false} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(120px,1fr))', gap: 8 }}>
            <MetricTile label="TEST LOSS" value={fnum(evalResult?.testLoss)} color={C.gold} />
            {prepared?.task === 'classification'
              ? <>
                <MetricTile label="TEST ACC" value={`${((evalResult?.testAcc || 0) * 100).toFixed(2)}%`} color={C.green} />
                <MetricTile label="VAL PRECISION" value={trainState?.valMetrics?.precision == null ? '-' : trainState.valMetrics.precision.toFixed(4)} color={C.cyan} />
                <MetricTile label="VAL F1" value={trainState?.valMetrics?.f1 == null ? '-' : trainState.valMetrics.f1.toFixed(4)} color={C.violet} />
              </>
              : <>
                <MetricTile label="RMSE" value={fnum(evalResult?.rmse)} color={C.green} />
                <MetricTile label="MAE" value={fnum(evalResult?.mae)} color={C.cyan} />
                <MetricTile label="R²" value={fnum(evalResult?.r2)} color={C.violet} />
              </>}
          </div>

          {prepared?.task === 'classification' && (
            <>
              <Card>
                <CardHead label="CLASSIFICATION REPORT (VALIDATION)" accent={C.green} />
                <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 520 }}>
                      <thead>
                        <tr style={{ background: C.deep }}>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Class</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Precision</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Recall</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>F1</th>
                          <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>Support</th>
                        </tr>
                      </thead>
                      <tbody>
                        {valReport.map((row) => (
                          <tr key={row.classIndex}>
                            <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{classLabels[row.classIndex] ?? `Class ${row.classIndex}`}</td>
                            <td style={{ padding: '6px 8px', color: C.green, borderBottom: `1px solid ${C.b1}` }}>{row.precision.toFixed(4)}</td>
                            <td style={{ padding: '6px 8px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{row.recall.toFixed(4)}</td>
                            <td style={{ padding: '6px 8px', color: C.violet, borderBottom: `1px solid ${C.b1}` }}>{row.f1.toFixed(4)}</td>
                            <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.support}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ height: 260, border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: 8 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={valReport.map((row) => ({
                        className: classLabels[row.classIndex] ?? `Class ${row.classIndex}`,
                        precision: row.precision,
                        recall: row.recall,
                        f1: row.f1,
                      }))} margin={{ top: 10, right: 8, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
                        <XAxis dataKey="className" stroke={C.t4} fontSize={9} interval={0} angle={-20} textAnchor="end" height={42} />
                        <YAxis stroke={C.t4} fontSize={9} domain={[0, 1]} />
                        <Tooltip content={<ChartTip />} />
                        <Legend wrapperStyle={{ fontSize: 9 }} />
                        <Bar dataKey="precision" fill={C.green} radius={[2, 2, 0, 0]} />
                        <Bar dataKey="recall" fill={C.cyan} radius={[2, 2, 0, 0]} />
                        <Bar dataKey="f1" fill={C.violet} radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>

              <Card>
                <CardHead label="CONFUSION MATRIX (VALIDATION)" accent={C.cyan} />
                <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 520 }}>
                      <thead>
                        <tr style={{ background: C.deep }}>
                          <th style={{ border: `1px solid ${C.b1}`, padding: 6, color: C.t4, fontFamily: mono }}>True \ Pred</th>
                          {classLabels.map((label) => (
                            <th key={label} style={{ border: `1px solid ${C.b1}`, padding: 6, color: C.t2, fontFamily: mono }}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {valConfusion.map((row, i) => (
                          <tr key={i}>
                            <td style={{ border: `1px solid ${C.b1}`, padding: 6, color: C.t2, fontFamily: mono }}>{classLabels[i] ?? `Class ${i}`}</td>
                            {row.map((val, j) => (
                              <td key={j} style={{ border: `1px solid ${C.b1}`, padding: 6, textAlign: 'center', color: i === j ? C.green : C.t2 }}>{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.raised, padding: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(classLabels.length, 1)}, minmax(32px,1fr))`, gap: 4 }}>
                      {valConfusion.flatMap((row, i) => row.map((val, j) => {
                        const p = val / Math.max(confusionMax, 1);
                        const fill = `rgba(34,211,238,${0.1 + p * 0.9})`;
                        return (
                          <div
                            key={`${i}-${j}`}
                            style={{
                              minHeight: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 3,
                              border: `1px solid ${C.b1}`,
                              background: fill,
                              color: p > 0.55 ? C.void : C.t1,
                              fontSize: 10,
                              fontFamily: mono,
                              fontWeight: 700,
                            }}
                          >
                            {val}
                          </div>
                        );
                      }))}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 9, color: C.t3, fontFamily: mono }}>Darker cell = higher count</div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 8 }}>
        <Btn onClick={exportReport} color={C.violet}>EXPORT JSON REPORT</Btn>
      </div>
    </div>
  );
}

// IDB session key for DL — module-level constant
const DL_IDB_KEY = 'dl_exp:local';

export default function DLExperimentsHub({ isAuthenticated, authUser, workspaceRestore, onWorkspaceRestored }) {
  const [tab, setTab] = useState('data');
  const [dataset, setDataset] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [checkpointing, setCheckpointing] = useState(false);
  const [workspaceId,   setWorkspaceId]   = useState(null);
  const [saveVersion,   setSaveVersion]   = useState(0);
  const [baseVersion,   setBaseVersion]   = useState(0);
  const [showSaveInfo,  setShowSaveInfo]  = useState(false);
  const [snapshotOnly,  setSnapshotOnly]  = useState(false);
  const [datasetName, setDatasetName] = useState('');
  const [pendingData, setPendingData] = useState(null);
  const [featureCfg, setFeatureCfg] = useState(DEFAULT_FEATURE_CFG);
  const [networkCfg, setNetworkCfg] = useState(DEFAULT_NETWORK_CFG);
  const [trainCfg, setTrainCfg] = useState(DEFAULT_TRAIN_CFG);
  const [trainState, setTrainState] = useState({
    epoch: 0,
    loss: null,
    valLoss: null,
    acc: null,
    valAcc: null,
    gradNorm: null,
    weightRms: null,
    trainMetrics: null,
    valMetrics: null,
    history: [],
    model: null,
    activations: null,
  });
  const [evalResult, setEvalResult] = useState(null);
  const [running, setRunning] = useState(false);
  const stopRef = useRef(false);
  const [dashboardCards, setDashboardCards] = useState([]);
  const [reportState, setReportState] = useState(null);
  const resetWorkspaceTracking = useCallback(() => {
    setWorkspaceId(null);
    setSaveVersion(0);
    setBaseVersion(0);
    setSnapshotOnly(false);
  }, []);
  const persistLocal = useCallback((data) => {
    const wsId = data?.workspaceId;
    idbSave(wsId ? `dl_exp:${wsId}` : DL_IDB_KEY, data).catch(() => {});
  }, []);

  // IDB auto-restore removed: every left-panel click = fresh start.
  // Workspace resume (from homepage) is handled below.
  const idbRestoredRef = useRef(false);

  // Passive step tracking: fires whenever the active tab changes while data is loaded.
  // Mirrors ML's pattern so auto-navigation (e.g. setTab('profile') after upload) also
  // counts as a workflow step. 'data' is excluded — 'upload' is sent in handleLoaded.
  useEffect(() => {
    if (!dataset) return;
    if (!DL_USAGE_WORKFLOW.includes(tab) || tab === 'data') return;
    usageStep('dl_experiments', tab, { tab }, DL_USAGE_WORKFLOW);
  }, [dataset, tab]);

  const tabs = DL_TABS(dataset);
  const displayTabs = useMemo(
    () => tabs.map((tabItem) => (tabItem.id === 'data' ? { ...tabItem, label: 'UPLOAD', icon: '⬆' } : tabItem)),
    [tabs]
  );

  const prepared = useMemo(() => buildEncodedDataset(dataset, featureCfg), [dataset, featureCfg]);
  const profile = useMemo(() => (dataset ? profileDataset(dataset) : null), [dataset]);

  const pinChart = useCallback((chart) => {
    setDashboardCards((prev) => {
      const exists = prev.some((item) => item?.chart?.id === chart?.id);
      if (exists) return prev.filter((item) => item?.chart?.id !== chart?.id);
      return [...prev, { chart, pinnedAt: Date.now() }];
    });
  }, []);

  const pinnedIds = useMemo(() => new Set(dashboardCards.map((item) => item.chart.id)), [dashboardCards]);

  const resetAll = useCallback(() => {
    setTab('data');
    setDataset(null);
    setDatasetName('');
    setPendingData(null);
    setFeatureCfg(DEFAULT_FEATURE_CFG);
    setNetworkCfg(DEFAULT_NETWORK_CFG);
    setTrainCfg(DEFAULT_TRAIN_CFG);
    setTrainState({ epoch: 0, loss: null, valLoss: null, acc: null, valAcc: null, gradNorm: null, weightRms: null, trainMetrics: null, valMetrics: null, history: [], model: null, activations: null });
    setEvalResult(null);
    setRunning(false);
    stopRef.current = false;
    setReportState(null);
    resetWorkspaceTracking();
  }, [resetWorkspaceTracking]);

  const handleLoaded = useCallback((ds, name) => {
    window.dispatchEvent(new CustomEvent('dd-collapse-left'));
    if (snapshotOnly) {
      // Restoring raw rows into a saved snapshot workspace — keep all trained results, configs, and workspace link.
      setDataset(ds);
      setDatasetName(name || 'dataset.csv');
      setReportState(null);
      setSnapshotOnly(false);
      setTab('profile');
      return;
    }
    resetWorkspaceTracking();
    setDataset(ds);
    setDatasetName(name || 'dataset.csv');
    setReportState(null);
    const target = ds.headers[ds.headers.length - 1] || ds.headers[0] || '';
    const features = ds.headers.filter((h) => h !== target);
    setFeatureCfg((prev) => ({ ...prev, target, features, task: inferTask(ds, target) }));
    setTrainState({ epoch: 0, loss: null, valLoss: null, acc: null, valAcc: null, gradNorm: null, weightRms: null, trainMetrics: null, valMetrics: null, history: [], model: null, activations: null });
    setEvalResult(null);
    startUsageSession('dl_experiments');
    usageStep('dl_experiments', 'upload', { rows: ds.rows.length, cols: ds.headers.length, dataset: name || 'dataset.csv' }, DL_USAGE_WORKFLOW);
    trackAction({
      module: 'dl_experiments',
      type: 'dl:dataset_loaded',
      label: 'Dataset loaded',
      group: 'data',
      icon: '📁',
      meta: {
        dataset: name || 'dataset.csv',
        rows: ds.rows.length,
        cols: ds.headers.length,
      },
      replayable: false,
    });
    setTab('profile');
  }, [resetWorkspaceTracking, snapshotOnly]);

  const onChangeTab = useCallback((next) => {
    const tabDef = tabs.find((t) => t.id === next);
    if (!tabDef || tabDef.disabled) return;
    startTransition(() => setTab(next));
    // Step is sent by the passive useEffect above; skip 'data' which is not in the workflow.
    trackAction({
      module: 'dl_experiments',
      type: `dl:tab_${next}`,
      label: `Open ${String(next).toUpperCase()} tab`,
      group: DL_TAB_GROUP[next] || 'misc',
      icon: '🗂️',
      meta: { tab: next },
      replayable: false,
    });
  }, [tabs]);

  const handleCleanedDataset = useCallback((newDs) => {
    if (!newDs?.rows?.length || !newDs?.headers?.length) return;
    setDataset(newDs);
    setFeatureCfg((prev) => {
      const headers = newDs.headers || [];
      const target = headers.includes(prev.target)
        ? prev.target
        : (headers[headers.length - 1] || headers[0] || '');
      let features = (prev.features || []).filter((column) => column !== target && headers.includes(column));
      if (!features.length) features = headers.filter((column) => column !== target);
      const task = prev.task === 'auto' ? inferTask(newDs, target) : prev.task;
      return { ...prev, target, features, task };
    });
    setTrainState({ epoch: 0, loss: null, valLoss: null, acc: null, valAcc: null, gradNorm: null, weightRms: null, trainMetrics: null, valMetrics: null, history: [], model: null, activations: null });
    setEvalResult(null);
  }, []);

  const startTrain = useCallback(async () => {
    if (!prepared || running) return;
    setRunning(true);
    stopRef.current = false;
    usageStep('dl_experiments', 'train', {
      task: prepared.task,
      input: prepared.inputSize,
      output: prepared.outputSize,
      epochs: trainCfg.epochs,
    }, DL_USAGE_WORKFLOW);
    trackAction({
      module: 'dl_experiments',
      type: 'dl:train_started',
      label: 'Training started',
      group: 'train',
      icon: '🏋️',
      meta: {
        task: prepared.task,
        input: prepared.inputSize,
        output: prepared.outputSize,
        epochs: trainCfg.epochs,
      },
      replayable: false,
    });

    const hidden = parseLayerString(networkCfg.hiddenLayers);
    const layerSizes = [prepared.inputSize, ...hidden, prepared.outputSize];
    const model = initParams(layerSizes, trainCfg.initializer);

    const state = {
      t: 0,
      mW: model.weights.map((w) => w.map((row) => row.map(() => 0))),
      vW: model.weights.map((w) => w.map((row) => row.map(() => 0))),
      mB: model.biases.map((b) => b.map(() => 0)),
      vB: model.biases.map((b) => b.map(() => 0)),
    };

    const history = [];
    let finalEval = null;
    let lastActivations = null;

    const shuffledCore = Array.from({ length: prepared.XTrain.length }, (_, i) => i);
    for (let i = shuffledCore.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledCore[i], shuffledCore[j]] = [shuffledCore[j], shuffledCore[i]];
    }

    const valRatio = clamp(trainCfg.valSplit ?? 0.2, 0.1, 0.3);
    const valCount = Math.max(1, Math.min(shuffledCore.length - 1, Math.floor(shuffledCore.length * valRatio)));
    const valIdx = shuffledCore.slice(0, valCount);
    const coreIdx = shuffledCore.slice(valCount);

    const XVal = valIdx.map((idx) => prepared.XTrain[idx]);
    const YVal = valIdx.map((idx) => prepared.YTrain[idx]);
    const XCore = coreIdx.map((idx) => prepared.XTrain[idx]);
    const YCore = coreIdx.map((idx) => prepared.YTrain[idx]);

    if (!XCore.length || !XVal.length) {
      setRunning(false);
      return;
    }

    for (let epoch = 1; epoch <= trainCfg.epochs; epoch += 1) {
      if (stopRef.current) break;

      const idx = Array.from({ length: XCore.length }, (_, i) => i);
      for (let i = idx.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [idx[i], idx[j]] = [idx[j], idx[i]];
      }

      let gradNormAcc = 0;
      let batches = 0;

      for (let start = 0; start < idx.length; start += trainCfg.batchSize) {
        const batch = idx.slice(start, start + trainCfg.batchSize);
        const accGrad = cloneGradLike(model.weights, model.biases);
        let batchLoss = 0;
        let batchCorrect = 0;
        let batchGradNorm = 0;

        batch.forEach((id) => {
          const x = XCore[id];
          const y = YCore[id];
          const hiddenActs = parseHiddenActivations(networkCfg.hiddenActivations, hidden.length, networkCfg.activation || 'relu');
          const cache = forwardPass(model, x, prepared.task, hiddenActs);
          lastActivations = cache.activations;
          const bp = backwardPass(model, cache, y, prepared.task, hiddenActs);
          batchGradNorm += bp.gradNorm;

          for (let l = 0; l < accGrad.dW.length; l += 1) {
            for (let j = 0; j < accGrad.dW[l].length; j += 1) {
              accGrad.dB[l][j] += bp.grads.dB[l][j];
              for (let k = 0; k < accGrad.dW[l][j].length; k += 1) {
                accGrad.dW[l][j][k] += bp.grads.dW[l][j][k];
              }
            }
          }
        });

        applyOptimizer(model, accGrad, Math.max(batch.length, 1), trainCfg, state);
        gradNormAcc += batchGradNorm / Math.max(batch.length, 1);
        batches += 1;
      }

      const wFlat = model.weights.flat(2);
      const weightRms = Math.sqrt(wFlat.reduce((s, v) => s + v * v, 0) / Math.max(wFlat.length, 1));

      const hiddenActs = parseHiddenActivations(networkCfg.hiddenActivations, hidden.length, networkCfg.activation || 'relu');
      const trainEval = evaluateSplit(model, prepared.task, hiddenActs, XCore, YCore, prepared.outputSize);
      const valEval = evaluateSplit(model, prepared.task, hiddenActs, XVal, YVal, prepared.outputSize);

      const point = {
        epoch,
        trainLoss: trainEval.loss,
        valLoss: valEval.loss,
        trainAcc: trainEval.acc,
        valAcc: valEval.acc,
        gradNorm: gradNormAcc / Math.max(batches, 1),
      };
      history.push(point);

      setTrainState({
        epoch,
        loss: point.trainLoss,
        valLoss: point.valLoss,
        acc: point.trainAcc,
        valAcc: point.valAcc,
        gradNorm: point.gradNorm,
        weightRms,
        trainMetrics: trainEval,
        valMetrics: valEval,
        history: [...history],
        model: {
          weights: model.weights.map((w) => w.map((row) => [...row])),
          biases: model.biases.map((b) => [...b]),
        },
        activations: lastActivations,
      });

      finalEval = evaluateModel(
        model,
        prepared,
        hiddenActs
      );
      setEvalResult(finalEval);
      await sleep(0);
    }

    setRunning(false);
    if (finalEval) {
      usageStep('dl_experiments', 'test', {
        test_loss: Number(finalEval.testLoss?.toFixed?.(6) || finalEval.testLoss || 0),
      }, DL_USAGE_WORKFLOW);
      trackAction({
        module: 'dl_experiments',
        type: 'dl:evaluation_ready',
        label: 'Evaluation completed',
        group: 'eval',
        icon: '📈',
        meta: {
          test_loss: Number(finalEval.testLoss?.toFixed?.(6) || finalEval.testLoss || 0),
        },
        replayable: false,
      });
    }
  }, [prepared, running, trainCfg, networkCfg.activation, networkCfg.hiddenLayers, networkCfg.hiddenActivations]);

  const stopTrain = () => {
    stopRef.current = true;
    setRunning(false);
  };

  const buildSnapshot = useCallback(() => {
    const lastHist = trainState.history[trainState.history.length - 1];
    return {
      dataset: {
        name: datasetName || 'dataset.csv',
        rows: dataset?.rows?.length || 0,
        cols: dataset?.headers?.length || 0,
        target: featureCfg.target,
        task: featureCfg.task,
      },
      architecture: {
        hidden_layers: networkCfg.hiddenLayers,
        hidden_activations: networkCfg.hiddenActivations,
        activation: networkCfg.activation,
        optimizer: trainCfg.optimizer,
        epochs: trainCfg.epochs,
        batch_size: trainCfg.batchSize,
        learning_rate: trainCfg.lr,
      },
      prepared: prepared ? {
        task: prepared.task,
        target: prepared.target,
        features: prepared.features || [],
        split: prepared.split,
        rows_used: prepared.rowsUsed,
        input_size: prepared.inputSize,
        output_size: prepared.outputSize,
        class_list: prepared.classList || [],
      } : null,
      metrics: {
        final_epoch: lastHist ? lastHist.epoch : null,
        train_loss: lastHist ? Number((lastHist.trainLoss || 0).toFixed(6)) : null,
        val_loss: lastHist ? Number((lastHist.valLoss || 0).toFixed(6)) : null,
        train_acc: lastHist ? Number((lastHist.trainAcc || 0).toFixed(4)) : null,
        val_acc: lastHist ? Number((lastHist.valAcc || 0).toFixed(4)) : null,
        test_loss: evalResult ? Number((evalResult.testLoss || 0).toFixed(6)) : null,
        test_acc: evalResult ? Number((evalResult.testAcc || 0).toFixed(4)) : null,
      },
      tabs_completed: tabs.filter(t => !t.disabled).map(t => t.id),
      // Rich report data
      col_headers: (dataset?.headers || []).slice(0, 60),
      sample_rows: (dataset?.rows || []).slice(0, 50).map(r =>
        Array.isArray(r) ? r.slice(0, 60) : (dataset?.headers || []).slice(0, 60).map(h => r[h] ?? null)
      ),
      training_history: (trainState.history || []).slice(0, 200).map(h => ({
        epoch: h.epoch,
        trainLoss: +Number(h.trainLoss || 0).toFixed(6),
        valLoss:   +Number(h.valLoss   || 0).toFixed(6),
        trainAcc:  +Number(h.trainAcc  || 0).toFixed(4),
        valAcc:    +Number(h.valAcc    || 0).toFixed(4),
      })),
      train_state: {
        epoch: trainState.epoch,
        loss: trainState.loss,
        valLoss: trainState.valLoss,
        acc: trainState.acc,
        valAcc: trainState.valAcc,
        gradNorm: trainState.gradNorm,
        weightRms: trainState.weightRms,
      },
      val_metrics: trainState?.valMetrics ? {
        precision: trainState.valMetrics.precision,
        recall: trainState.valMetrics.recall,
        f1: trainState.valMetrics.f1,
        report: trainState.valMetrics.report || [],
        confusion: trainState.valMetrics.confusion || [],
      } : null,
      ml_config: {
        targetCol: prepared?.target || '',
        featureCols: prepared?.features || [],
        encodings: {},
        scalingMethod: {},
      },
      eval_result: evalResult ? {
        testLoss: +Number(evalResult.testLoss || 0).toFixed(6),
        testAcc:  +Number(evalResult.testAcc  || 0).toFixed(4),
        mae: evalResult.mae != null ? +Number(evalResult.mae).toFixed(6) : null,
        mse: evalResult.mse != null ? +Number(evalResult.mse).toFixed(6) : null,
        rmse: evalResult.rmse != null ? +Number(evalResult.rmse).toFixed(6) : null,
        r2: evalResult.r2 != null ? +Number(evalResult.r2).toFixed(6) : null,
      } : null,
      report_state: reportState,
    };
  }, [dataset, datasetName, featureCfg, networkCfg, trainCfg, trainState, evalResult, tabs, prepared, reportState]);

  // Handle workspace restore from WorkspaceDock
  useEffect(() => {
    if (!workspaceRestore || workspaceRestore.module_id !== 'dl_experiments') return;
    idbRestoredRef.current = true;
    const requestedVersion = Number(workspaceRestore.version_no || 0) || null;

    const fetchAndRestore = async () => {
      try {
        const [result, perWsLocal, flatLocal] = await Promise.all([
          requestedVersion && workspaceRestore.id
            ? workspaceApi.get(workspaceRestore.id, { version: requestedVersion })
            : workspaceApi.get(workspaceRestore.id),
          idbLoad(`dl_exp:${workspaceRestore.id}`),
          idbLoad(DL_IDB_KEY),
        ]);
        // Per-workspace key takes priority; flat key used for backward compat.
        const localSaved = perWsLocal || (flatLocal?.workspaceId === workspaceRestore.id ? flatLocal : null);
        const version = result?.workspace?.selected_version;
        const snap = version?.snapshot;
        const ws   = result?.workspace;
        console.log('[DL restore] snap:', !!snap, 'ws:', !!ws, 'localSaved:', !!localSaved,
          'snap.featureCfg:', !!snap?.featureCfg, 'snap.networkCfg:', !!snap?.networkCfg,
          'snap.trainCfg:', !!snap?.trainCfg, 'localSaved rows:', localSaved?.dataset?.rows?.length ?? 'none');
        if (snap) {
          // Server snapshot (_safe_snapshot at line 1541) strips only `rows` keys — it KEEPS
          // dataset.headers, featureCfg, networkCfg, trainCfg, evalResult (preds already
          // stripped client-side before upload), dashboardCards, tab.
          // IDB (localSaved) is the only source for raw dataset rows.
          const expectedHeaders = snap.dataset?.headers || localSaved?.dataset?.headers || [];
          const rawLocalRows    = localSaved?.dataset?.rows || [];
          const localRows       = expectedHeaders.length
            ? validateRowHeaders(rawLocalRows, expectedHeaders)
            : rawLocalRows;
          const nextVersion = version.version_no || requestedVersion || 1;
          const nextTab     = workspaceRestore.active_tab || snap.tab || localSaved?.tab || 'data';
          const nextDataset = expectedHeaders.length
            ? { name: snap.dataset?.name || localSaved?.dataset?.name || '', headers: expectedHeaders, rows: localRows }
            : (localSaved?.dataset ? { ...localSaved.dataset, rows: localRows } : null);
          if (nextDataset) setDataset(nextDataset);
          setDatasetName(snap.datasetName || localSaved?.datasetName || '');
          // Configs: server snapshot is primary (always written at save time).
          // IDB is fallback for cases where server had a size-limit failure (snap exists but configs are stripped).
          if (snap.featureCfg || localSaved?.featureCfg) setFeatureCfg(snap.featureCfg || localSaved?.featureCfg);
          if (snap.networkCfg || localSaved?.networkCfg) setNetworkCfg(snap.networkCfg || localSaved?.networkCfg);
          if (snap.trainCfg   || localSaved?.trainCfg)   setTrainCfg(snap.trainCfg   || localSaved?.trainCfg);
          if (snap.evalResult || localSaved?.evalResult)  setEvalResult(snap.evalResult || localSaved?.evalResult);
          if (localSaved?.trainState?.history?.length)    setTrainState(prev => ({ ...prev, ...localSaved.trainState }));
          setDashboardCards(snap.dashboardCards || localSaved?.dashboardCards || []);
          setWorkspaceId(workspaceRestore.id);
          setSaveVersion(nextVersion);
          setBaseVersion(nextVersion);
          setSnapshotOnly(localRows.length === 0);
          setTab(nextTab);
          if (localRows.length > 0) {
            const { activations: _a, ...trainStateForIdb } = localSaved?.trainState || {};
            persistLocal({
              dataset: nextDataset, datasetName: snap.datasetName || localSaved?.datasetName || '',
              featureCfg: snap.featureCfg || localSaved?.featureCfg,
              networkCfg: snap.networkCfg || localSaved?.networkCfg,
              trainCfg:   snap.trainCfg   || localSaved?.trainCfg,
              evalResult: snap.evalResult || localSaved?.evalResult,
              trainState: Object.keys(trainStateForIdb).length ? trainStateForIdb : undefined,
              dashboardCards: snap.dashboardCards || localSaved?.dashboardCards || [],
              tab: nextTab, workspaceId: workspaceRestore.id, saveVersion: nextVersion, baseVersion: nextVersion,
            });
          }
        } else if (ws) {
          // No server snapshot (saved before current format, or snapshot exceeded 512 KB).
          // Fall back entirely to IDB.
          if (localSaved?.dataset) {
            const nextVersion = localSaved.saveVersion || version?.version_no || 1;
            const nextTab     = workspaceRestore.active_tab || localSaved.tab || 'data';
            const localRows   = localSaved.dataset.rows || [];
            setDataset(localSaved.dataset);
            setDatasetName(localSaved.datasetName || '');
            if (localSaved.featureCfg) setFeatureCfg(localSaved.featureCfg);
            if (localSaved.networkCfg) setNetworkCfg(localSaved.networkCfg);
            if (localSaved.trainCfg)   setTrainCfg(localSaved.trainCfg);
            if (localSaved.evalResult) setEvalResult(localSaved.evalResult);
            if (localSaved.trainState?.history?.length) setTrainState(prev => ({ ...prev, ...localSaved.trainState }));
            setDashboardCards(localSaved.dashboardCards || []);
            setWorkspaceId(workspaceRestore.id);
            setSaveVersion(nextVersion);
            setBaseVersion(nextVersion);
            setSnapshotOnly(localRows.length === 0);
            setTab(nextTab);
          } else {
            // Nothing in IDB either — show upload prompt, preserve workspace link.
            const fallbackName = ws.summary?.filename || ws.label || 'dataset.csv';
            setDataset({ name: fallbackName, headers: [], rows: [] });
            setDatasetName(fallbackName);
            setWorkspaceId(workspaceRestore.id);
            setSaveVersion(version?.version_no || 1);
            setBaseVersion(version?.version_no || 1);
            setSnapshotOnly(true);
            setTab('data');
          }
        }
      } catch (err) {
        // Log so we can see what's actually failing in the browser console
        console.error('[DL restore] fetchAndRestore threw:', err);
      }
      onWorkspaceRestored?.();
    };
    fetchAndRestore();
  }, [workspaceRestore]);

  const doSave = useCallback(async () => {
    if (!dataset || !isAuthenticated) return;
    setCheckpointing(true);
    try {
      const lastHist = trainState.history[trainState.history.length - 1];
      const payload = {
        parent_version: baseVersion || saveVersion || undefined,
        active_tab: tab,
        summary: {
          filename: datasetName || '',
          rows: dataset.rows?.length || 0,
          cols: dataset.headers?.length || 0,
          target_col: featureCfg.target || '',
          task_type: featureCfg.task || '',
          best_score: evalResult?.testAcc != null ? evalResult.testAcc : (lastHist?.valAcc ?? undefined),
        },
        meta: { tabs_visited: tabs.filter(t => !t.disabled).map(t => t.id) },
        actions: getWorkspaceActionSummary('dl_experiments', authUser),
      };
      let newId = workspaceId;
      let nextVersion = saveVersion;
      // Strip per-row prediction array (preds) from evalResult so snapshot stays under 512 KB backend limit.
      const evalSnap = evalResult ? (({ preds, ...rest }) => rest)(evalResult) : null;
      const makeSnap = () => ({ dataset: dataset ? { name: dataset.name, headers: dataset.headers } : null, datasetName, featureCfg, networkCfg, trainCfg, evalResult: evalSnap, dashboardCards, tab });
      if (!workspaceId) {
        const result = await workspaceApi.save({ module_id: 'dl_experiments', label: datasetName || 'DL Experiment', ...payload, snapshot: makeSnap() });
        newId = result.id;
        nextVersion = result.version_no || 1;
        setWorkspaceId(newId);
      } else {
        const result = await workspaceApi.update(workspaceId, { ...payload, snapshot: makeSnap() });
        nextVersion = result.version_no || (saveVersion + 1);
      }
      setSaveVersion(nextVersion);
      setBaseVersion(nextVersion);
      setSnapshotOnly(false);
      const evalForIdb = evalResult ? (({ preds, ...rest }) => rest)(evalResult) : null;
      const { activations: _a, ...trainStateForIdb } = trainState;
      persistLocal({ dataset, datasetName, featureCfg, networkCfg, trainCfg, evalResult: evalForIdb, trainState: trainStateForIdb, dashboardCards, tab, workspaceId: newId, saveVersion: nextVersion, baseVersion: nextVersion });
      window.dispatchEvent(new CustomEvent('dd-workspace-saved'));
    } catch (e) {
      // silent — best-effort
    } finally {
      setCheckpointing(false);
    }
  }, [dataset, datasetName, featureCfg, networkCfg, trainCfg, tab, trainState, evalResult, tabs, workspaceId, saveVersion, baseVersion, dashboardCards, isAuthenticated, authUser, persistLocal]);

  const handleCheckpoint = useCallback(() => {
    if (saveVersion === 0) { setShowSaveInfo(true); } else { doSave(); }
  }, [saveVersion, doSave]);

  useEffect(() => {
    startUsageSession('dl_experiments');
    trackAction({
      module: 'dl_experiments',
      type: 'dl:workspace_opened',
      label: 'DL workspace opened',
      group: 'explore',
      icon: '🧠',
      replayable: false,
    });
  }, []);

  useEffect(() => {
    const onNew = () => resetAll();
    const onReset = () => resetAll();
    const onLoadUrl = (event) => {
      const url = event?.detail?.url;
      if (!url) return;
      const name = event?.detail?.name || (String(url).split('/').pop() || 'dataset.csv');
      resetAll();
      setPendingData({ id: Date.now(), type: 'url', url: String(url), name: String(name) });
    };
    const onLoadCsv = (event) => {
      const csvText = event?.detail?.csvText;
      if (!csvText) return;
      const name = event?.detail?.name || 'dataset.csv';
      resetAll();
      setPendingData({ id: Date.now(), type: 'csv', csvText: String(csvText), name: String(name) });
    };
    window.addEventListener('dd:dl_exp:new', onNew);
    window.addEventListener('dd:dl_exp:reset', onReset);
    window.addEventListener('dd:dl_exp:load_url', onLoadUrl);
    window.addEventListener('dd:dl_exp:load_csv', onLoadCsv);
    return () => {
      window.removeEventListener('dd:dl_exp:new', onNew);
      window.removeEventListener('dd:dl_exp:reset', onReset);
      window.removeEventListener('dd:dl_exp:load_url', onLoadUrl);
      window.removeEventListener('dd:dl_exp:load_csv', onLoadCsv);
    };
  }, [resetAll]);

  if (workspaceRestore && workspaceRestore.module_id === 'dl_experiments') return (
    <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--void)' }}>
      <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, color:'var(--t3)', letterSpacing:'.06em' }}>⏳ RESTORING WORKSPACE…</div>
    </div>
  );

  return (
    <PinContext.Provider value={{ pinChart, pinnedIds }}>
      <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: C.void }}>
        <style>{WS_GLOBAL_STYLE}</style>
        {snapshotOnly && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', background:'#fbbf2412', borderBottom:'1px solid #fbbf2435', flexShrink:0, fontFamily:'JetBrains Mono,monospace', fontSize:10, color:'#fbbf24' }}>
            <span>&#9888;</span>
            <span>Raw data not in this browser — re-upload your dataset to reactivate training. All saved configurations and evaluation results are preserved.</span>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <div style={{ background: C.deep, borderBottom: `1px solid ${C.b0}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '4px 6px', gap: 2 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1, gap: 2 }}>
                {displayTabs.map((tabItem) => {
                  const active = tabItem.id === tab;
                  return (
                    <button
                      key={tabItem.id}
                      onClick={() => !tabItem.disabled && onChangeTab(tabItem.id)}
                      disabled={tabItem.disabled}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '8px 11px', border: 'none', background: 'transparent',
                        color: active ? (tabItem.color || C.cyan) : (tabItem.disabled ? C.t4 : C.t3),
                        borderBottom: `2px solid ${active ? (tabItem.color || C.cyan) : 'transparent'}`,
                        fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: active ? 700 : 400,
                        cursor: tabItem.disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', letterSpacing: '.08em',
                        opacity: tabItem.disabled ? 0.5 : 1, transition: 'all .1s',
                      }}
                    >
                      {tabItem.icon && <span style={{ fontSize: 12 }}>{tabItem.icon}</span>}
                      <span>{tabItem.label}</span>
                    </button>
                  );
                })}
              </div>
              {isAuthenticated && dataset && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={handleCheckpoint}
                    disabled={checkpointing}
                    style={{
                      padding: '5px 12px', border: `1px solid ${C.cyan}50`, background: `${C.cyan}18`,
                      color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700,
                      letterSpacing: '.08em', borderRadius: 3, cursor: checkpointing ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap', flexShrink: 0, opacity: checkpointing ? 0.6 : 1,
                    }}
                  >
                    {checkpointing ? '⏳ SAVING…' : saveVersion > 0 ? `💾 SAVE VERSION [${saveVersion}]` : '💾 SAVE VERSION'}
                  </button>
                  <button
                    onClick={() => setPublishOpen(true)}
                    style={{
                      padding: '5px 12px', border: `1px solid ${C.green}`, background: `${C.green}18`,
                      color: C.green, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700,
                      letterSpacing: '.08em', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    📤 PUBLISH
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: tab === 'data' ? 0 : 18, minHeight: 0 }}>
            {tab === 'data' && (
              <DataSourceTab
                onLoaded={handleLoaded}
                autoUrl={pendingData?.type === 'url' ? pendingData.url : null}
                autoCsv={pendingData?.type === 'csv' ? pendingData : null}
                onAutoConsumed={() => setPendingData(null)}
              />
            )}

            {tab === 'profile' && dataset && profile && <ProfileTab ds={dataset} profile={profile} />}
            {tab === 'clean' && dataset && <CleaningLab ds={dataset} onUpdate={handleCleanedDataset} />}

            {tab === 'feature' && dataset && <DLFeatureTabFromML ds={dataset} cfg={featureCfg} setCfg={setFeatureCfg} dashboardCards={dashboardCards} />}
            {tab === 'network' && dataset && <NetworkTab data={prepared} cfg={networkCfg} setCfg={setNetworkCfg} trainCfg={trainCfg} setTrainCfg={setTrainCfg} />}
            {tab === 'init' && dataset && <InitTab cfg={trainCfg} setCfg={setTrainCfg} prepared={prepared} networkCfg={networkCfg} trainState={trainState} dashboardCards={dashboardCards} />}
            {tab === 'train' && dataset && (
              <TrainTab
                prepared={prepared}
                networkCfg={networkCfg}
                trainCfg={trainCfg}
                setTrainCfg={setTrainCfg}
                trainState={trainState}
                onStart={startTrain}
                onStop={stopTrain}
                running={running}
                dashboardCards={dashboardCards}
              />
            )}
            {tab === 'test' && dataset && <TestTab prepared={prepared} trainState={trainState} networkCfg={networkCfg} dashboardCards={dashboardCards} />}
            {tab === 'metrics' && dataset && <MetricsTab history={trainState.history} prepared={prepared} evalResult={evalResult} dashboardCards={dashboardCards} />}
            {tab === 'report' && (
              <ReportTab
                ds={dataset}
                prepared={prepared}
                networkCfg={networkCfg}
                trainCfg={trainCfg}
                trainState={trainState}
                evalResult={evalResult}
                onMarkDone={() => usageDone('dl_experiments', 'report_download', 1, { rows: dataset?.rows?.length || 0 }, DL_USAGE_WORKFLOW)}
                dashboardCards={dashboardCards}
                profile={profile}
                reportState={reportState}
                onReportStateChange={setReportState}
              />
            )}
          </div>
        </div>
      </div>
      {showSaveInfo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#111', border:`1px solid ${C.cyan}40`, borderRadius:8, padding:'28px 32px', maxWidth:440, fontFamily:'JetBrains Mono,monospace' }}>
            <div style={{ color:C.cyan, fontSize:13, fontWeight:700, marginBottom:12 }}>💾 SAVE VERSION</div>
            <div style={{ color:'#ccc', fontSize:11, lineHeight:1.8, marginBottom:20 }}>
              Raw uploaded data stays <strong style={{ color:'#fff' }}>in this browser</strong>.{' '}
              Saved versions store a lightweight experiment snapshot and workflow history on the server.{' '}
              On another device, you may still need to re-upload the same dataset to retrain the network.
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowSaveInfo(false)} style={{ padding:'6px 14px', background:'transparent', border:'1px solid #444', color:'#888', fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer' }}>
                CANCEL
              </button>
              <button onClick={() => { setShowSaveInfo(false); doSave(); }} style={{ padding:'6px 14px', background:`${C.cyan}20`, border:`1px solid ${C.cyan}60`, color:C.cyan, fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer', fontWeight:700 }}>
                GOT IT · SAVE
              </button>
            </div>
          </div>
        </div>
      )}
      {publishOpen && (
        <PublishExperimentModal
          snapshot={buildSnapshot()}
          expType="dl_experiments"
          onClose={() => setPublishOpen(false)}
          onPublished={() => setPublishOpen(false)}
        />
      )}
    </PinContext.Provider>
  );
}
